const _ = require("lodash");
const Color = require("color");
const ColorUtil = require("../util/ColorUtil");
const colors = ColorUtil.getDiagramColors();
const colorSchemes = ColorUtil.getCurrentScheme();
const Font = require("../util/Font");
const CanvasText = require("../util/CanvasText");
const MultiplePattern = require("../util/MultiplePattern");

const LineLayout = require("./LineLayout");
const Operators = require("./Operators");
const NodesLongOps = require("./NodesLongOps");
const NodesArrows = require("./NodesArrows");
const NodesImages = require("./NodesImages");
const NodesEnvironments = require("./NodesEnvironments");
const NodesCustom = require("./NodesCustom");
const {
	drawArrowHead,
	calculateArrowHeadPositions,
} = require("../util/ArrowDraw");
const {drawInputCircleButton} = require("../util/DrawButtonUtil");
const CircleCropUtil = require("../util/CircleCropUtil");

const Helpers = require("./util/RenderHelpers");
// Alias commonly used helpers
const {
	scaleFont,
	computeNodeFullWidth,
	getLineWidth,
	getDigitWidth,
	getSpaceWidth,
	calculateAdditionalBottomSpace,
} = Helpers;

const DrawRectUtil = require("../util/DrawRectangleUtil");
const CanvasUtil = require("../util/CanvasUtil");
const Math2D = require("../util/Math2D");

const CommandIndex = require("./CommandIndex");

// Defines behavior for each node type. The keys of nodeData are types of nodes
// and the values are objects with
// - isBlock: true if the node is a block element. Defaults to false.
// - draw: function taking context, x, y, node, measure which draws the node.
//   rc.drawNode fills measure in if not called with it.
// - measure: function taking context, props, node which measures the node.
//   Returns object with width, top, bottom, props for non-block nodes and
//   object with width, height, props for block nodes.
const nodeData = {
	text: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			context.font = Font.fromInfo(measure.fontInfo);
			CanvasText.drawMiddleText(context, node.text, x, y);
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			context.font = Font.fromInfo(fontInfo);
			return {
				width: context.measureText(node.text).width,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
				fontInfo,
			};
		},
	},
	error: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const fontSize = 10;
			const {padding, width, top, bottom} = measure;
			context.save();
			context.font = fontSize + "px sans-serif";
			context.strokeStyle = "#FF0000";
			context.strokeRect(x - 0.5, y - top, width, top + bottom);
			context.fillStyle = "#FF0000";
			CanvasText.drawMiddleText(context, node.name, x + padding, y);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const fontSize = 10;
			const padding = 2.5;
			context.font = fontSize + "px sans-serif";
			return {
				width: context.measureText(node.name).width + 2 * padding,
				top: fontSize / 2 + padding,
				bottom: fontSize / 2 + padding,
				fontInfo,
				// extra
				padding: padding,
			};
		},
	},
	word: {
		draw(rc, x, y, node, measure) {
			return rc.drawNode(rc, x, y, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			return rc.measureNode(rc, fontInfo, props, node.children[0]);
		},
	},
	center: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			rc.drawNode(rc, x, y + measure.top, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const childMeasure = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			return _.extend(childMeasure, {
				height: childMeasure.top + childMeasure.bottom,
			});
		},
	},
	space: {
		draw() {
			// Do nothing.
		},
		measure(rc, fontInfo, props) {
			return {
				width: getSpaceWidth(rc, fontInfo),
				top: 0,
				bottom: 0,
				fontInfo,
			};
		},
	},
	lineBreak: {
		draw() {
			// Do nothing.
		},
		measure(rc, fontInfo, props) {
			return {
				width: 0,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
				fontInfo,
			};
		},
	},
	quad: {
		draw() {
			// Do nothing.
		},
		measure(rc, fontInfo, props, node) {
			return {
				width: fontInfo.size * node.scale,
				top: 0,
				bottom: 0,
				fontInfo,
			};
		},
	},
	indent: {
		draw() {
			// Do nothing.
		},
		measure(rc, fontInfo, props, node) {
			return {
				width: fontInfo.size * 2,
				top: 0,
				bottom: 0,
				fontInfo,
			};
		},
	},
	operator: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, text} = measure;
			let newY = y;
			if (node.isUnary && text === "-") {
				// Shorter unary dash is not vertically centered, so we center it manually.
				newY = y - fontInfo.size / 24;
			} else if (text === "\u2022") {
				// The bullet for cdot is also not centered, so we center it as well
				newY = y + fontInfo.size / 12;
			}
			context.font = Font.fromInfo(fontInfo);
			CanvasText.drawMiddleText(context, text, x, newY);
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const text = Operators.computeText(node.text, node);
			context.font = Font.fromInfo(fontInfo);
			const xPadding = node.isUnary ? fontInfo.size / 40 : fontInfo.size / 5;
			return {
				width: context.measureText(text).width,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
				fontInfo,
				xPadding,
				// extra
				text,
			};
		},
	},
	variable: {
		draw(rc, x, y, node, measure) {
			const {yOffset, varPadding} = measure;
			rc.drawNode(rc, x + varPadding, y + yOffset, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const varFontInfo = Helpers.toVariableFont(fontInfo);
			const innerMeasure = rc.measureNode(
				rc,
				varFontInfo,
				props,
				node.children[0]
			);
			const varPadding = fontInfo.size / 12;
			// Variables draw at a different font size than other things, which means
			// the middle baseLine text adjustment is thrown off. yOffset corrects it.
			const origFont = Font.contextizeFont(Font.fromInfo(fontInfo));
			const varFont = Font.contextizeFont(Font.fromInfo(varFontInfo));
			const yOffset =
				CanvasText.getMiddleTextShift(origFont) -
				CanvasText.getMiddleTextShift(varFont);
			const varMeasure = _.extend({}, innerMeasure, {
				width: innerMeasure.width + 2 * varPadding,
				varPadding: varPadding,
				yOffset,
			});
			return varMeasure;
		},
	},
	light: {
		draw(rc, x, y, node, measure) {
			const white = Color("#FFFFFF");
			const lightenColor = function(colorHex) {
				return Color(colorHex)
					.mix(white, 0.5)
					.hex();
			};

			const context = rc.context;
			context.save();
			context.fillStyle = lightenColor(context.fillStyle);
			context.strokeStyle = lightenColor(context.strokeStyle);
			rc.drawNode(rc, x, y, node.children[0]);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			return rc.measureNode(rc, fontInfo, props, node.children[0]);
		},
	},
	bigDecimal: {
		draw(rc, x, y, node, measure) {
			rc.context.save();
			rc.context
				.bp()
				.arc(
					x + measure.width / 2,
					y + measure.height / 2 - measure.width / 2,
					measure.width / 2,
					0,
					2 * Math.PI
				)
				.fill(colors[node.color]);
			rc.context.restore();
		},
		measure(rc, fontInfo, props, node) {
			return {
				width: fontInfo.size / 4,
				height: fontInfo.size,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
			};
		},
	},
	dots: {
		draw(rc, x, y, node, measure) {
			const {fontInfo, spaceWidth, dotWidth} = measure;
			const context = rc.context;
			context.save();
			if (node.dotStyle === "c") {
				y -= CanvasText.getMiddleTextShift(context.font);
			}
			context.font = Font.fromInfo(fontInfo);
			let currX = x + spaceWidth;
			for (let i = 0; i < 3; i++) {
				CanvasText.drawMiddleText(context, ".", currX, y);
				currX += dotWidth + spaceWidth;
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			const spaceWidth = context.measureText(" ").width / 2;
			const dotWidth = context.measureText(".").width;
			context.restore();
			return {
				width: spaceWidth * 5 + dotWidth * 3,
				top: fontInfo.size / 2,
				bottom: fontInfo.size / 2,
				fontInfo,
				spaceWidth,
				dotWidth,
			};
		},
	},
	vdots: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo} = measure;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			// var baselines = ["bottom", "middle", "top"];
			context.textBaseline = "top";
			for (let i = 0; i < 3; i++) {
				y -= fontInfo.size / 2;
				context.fillText(" . ", x, y);
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			const width = context.measureText(" . ").width;
			context.restore();
			return {
				width: width,
				top: (fontInfo.size * 3) / 4,
				bottom: fontInfo.size / 2,
				fontInfo,
			};
		},
	},
	triangle: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {width, top, bottom} = measure;
			context
				.bp()
				.M(x, y - top)
				.L(x + width, y)
				.L(x, y + bottom)
				.Z()
				.fill();
		},
		measure(rc, fontInfo, props, node) {
			const size = fontInfo.size * 0.8;
			return {
				width: (size * Math.sqrt(3)) / 2,
				top: size / 2,
				bottom: size / 2,
				fontInfo,
			};
		},
	},
	mequal: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			context.font = Font.fromInfo(measure.fontInfo);
			context.save();
			context.globalAlpha = 0.3;
			CanvasText.drawMiddleText(context, "=", x, y);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const fontSize = fontInfo.size;
			const width = rc.context.measureText("=").width;
			return {
				width,
				top: fontSize / 2,
				bottom: fontSize / 2,
				fontInfo,
			};
		},
	},
	superscript: {
		draw(rc, x, y, node, measure) {
			const {baseWidth, yOffset, expPadding} = measure;
			rc.drawNode(rc, x, y, node.children[0]);
			rc.drawNode(
				rc,
				x + expPadding + baseWidth,
				y + yOffset,
				node.children[1]
			);
		},
		measure(rc, fontInfo, props, node) {
			const baseMeasured = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			const expFontInfo = scaleFont(fontInfo, 0.675);
			const expMeasured = rc.measureNode(
				rc,
				expFontInfo,
				props,
				node.children[1]
			);
			const expPadding = fontInfo.size / 24;

			const yOffset = -(baseMeasured.top - 0.1 * fontInfo.size);
			return {
				width: baseMeasured.width + expMeasured.width + 2 * expPadding,
				top: Math.max(baseMeasured.top, -yOffset + expMeasured.top),
				bottom: Math.max(baseMeasured.bottom, yOffset + expMeasured.bottom),
				fontInfo,
				// extra
				expPadding,
				yOffset,
				baseWidth: baseMeasured.width,
			};
		},
	},
	fraction: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				childFontInfo,
				width,
				numWidth,
				denomWidth,
				numBottom,
				denomTop,
			} = measure;
			const baseWidth = getLineWidth(rc, childFontInfo);

			const numX = x + (width - numWidth) / 2;
			let numY = y - numBottom;
			numY -=
				baseWidth * (node.children[0].children[0].type === "blank" ? 2 : 1);
			rc.drawNode(rc, numX, numY, node.children[0]);

			const denomX = x + (width - denomWidth) / 2;
			let denomY = y + denomTop;
			denomY +=
				baseWidth * (node.children[1].children[0].type === "blank" ? 2 : 1);
			rc.drawNode(rc, denomX, denomY, node.children[1]);

			context.save();
			context.lineWidth = baseWidth;
			context.beginPath();
			context.moveTo(x, y);
			context.lineTo(x + width, y);
			context.stroke();
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const xPadding = fontInfo.size / 12;
			const childFontInfo = scaleFont(fontInfo, 0.9);
			const numMeasured = rc.measureNode(
				rc,
				childFontInfo,
				props,
				node.children[0]
			);
			const denomMeasured = rc.measureNode(
				rc,
				childFontInfo,
				props,
				node.children[1]
			);
			const lineWidth = getLineWidth(rc, fontInfo);

			const totalWidth =
				Math.max(numMeasured.width, denomMeasured.width) + 2 * xPadding;
			const extraBottomNumSpace = calculateAdditionalBottomSpace(
				node.children[0],
				lineWidth
			);
			const extraBottomDenomSpace = calculateAdditionalBottomSpace(
				node.children[1],
				lineWidth
			);
			const numHeight =
				numMeasured.top + numMeasured.bottom + extraBottomNumSpace;
			const denomHeight =
				denomMeasured.top + denomMeasured.bottom + extraBottomDenomSpace;

			return {
				width: totalWidth,
				top: numHeight + lineWidth,
				bottom: denomHeight + 2 * lineWidth,
				fontInfo,
				xPadding,
				// extra
				childFontInfo,
				yPadding: fontInfo.size / 48,
				numWidth: numMeasured.width,
				denomWidth: denomMeasured.width,
				numBottom: numMeasured.bottom + extraBottomNumSpace + lineWidth / 2,
				denomTop: denomMeasured.top + lineWidth,
			};
		},
	},
	underlabel: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				contentFontInfo,
				width,
				contentWidth,
				labelWidth,
				contentHeight,
				labelHeight,
				yOffset,
			} = measure;
			const baseWidth = getLineWidth(rc, contentFontInfo);

			const lineY = y - yOffset + contentHeight / 2;

			const contentX = x + (width - contentWidth) / 2;
			let contentY = y - yOffset;
			contentY -=
				baseWidth * (node.children[0].children[0].type === "blank" ? 4 : 1);
			rc.drawNode(rc, contentX, contentY, node.children[0]);

			const labelX = x + (width - labelWidth) / 2;
			let labelY = lineY + labelHeight / 2;
			labelY +=
				baseWidth * (node.children[1].children[0].type === "blank" ? 4 : 1);
			rc.drawNode(rc, labelX, labelY, node.children[1]);

			context.save();
			context.lineWidth = baseWidth;
			context.beginPath();
			context.moveTo(x, lineY);
			context.lineTo(x + width, lineY);
			context.stroke();
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const fontSize = fontInfo.size;
			const xPadding = fontSize / 12;
			const yOffset = fontSize / 8;
			const contentFontInfo = scaleFont(fontInfo, 0.9);
			const labelFontInfo = scaleFont(fontInfo, 0.7);
			const contentMeasured = rc.measureNode(
				rc,
				contentFontInfo,
				props,
				node.children[0]
			);
			const labelMeasured = rc.measureNode(
				rc,
				labelFontInfo,
				props,
				node.children[1]
			);
			const lineWidth = getLineWidth(rc, fontInfo);

			const totalWidth = Math.max(contentMeasured.width, labelMeasured.width);
			const extraBottomContentSpace = calculateAdditionalBottomSpace(
				node.children[0],
				lineWidth
			);
			const extraBottomLabelSpace = calculateAdditionalBottomSpace(
				node.children[1],
				lineWidth
			);
			const contentHeight =
				contentMeasured.top + contentMeasured.bottom + extraBottomContentSpace;
			const labelHeight =
				labelMeasured.top + labelMeasured.bottom + extraBottomLabelSpace;

			return {
				width: totalWidth,
				height: contentHeight + labelHeight + 3 * lineWidth + yOffset,
				fontInfo,
				xPadding,
				//extra
				contentFontInfo,
				contentWidth: contentMeasured.width,
				labelWidth: labelMeasured.width,
				contentHeight,
				labelHeight,
				yOffset, //offset that content is off from center
			};
		},
	},
	blockList: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const {childMeasures, maxTop, height} = measure;
			let currX = x;
			let align = "center";
			if (node.alignment) {
				align = node.alignment;
			}
			for (let i = 0; i < node.children.length; i++) {
				if (align === "center") {
					if (childMeasures[i].height) {
						rc.drawNode(
							rc,
							currX,
							y + maxTop - childMeasures[i].height / 2,
							node.children[i]
						);
					} else {
						rc.drawNode(rc, currX, y + maxTop, node.children[i]);
					}
				} else if (align === "alignTop") {
					if (childMeasures[i].height) {
						rc.drawNode(rc, currX, y, node.children[i]);
					} else {
						rc.drawNode(rc, currX, y + childMeasures[i].top, node.children[i]);
					}
				} else {
					if (childMeasures[i].height) {
						rc.drawNode(
							rc,
							currX,
							y + height - childMeasures[i].height,
							node.children[i]
						);
					} else {
						rc.drawNode(
							rc,
							currX,
							y + height - childMeasures[i].bottom,
							node.children[i]
						);
					}
				}
				currX += childMeasures[i].width;
			}
		},
		measure(rc, fontInfo, props, node) {
			let maxTop = 0;
			let maxBottom = 0;
			let width = 0;
			const childMeasures = [];
			for (let i = 0; i < node.children.length; i++) {
				childMeasures.push(
					rc.measureNode(rc, fontInfo, props, node.children[i])
				);
				width += childMeasures[i].width;
				if (childMeasures[i].height) {
					maxTop = Math.max(maxTop, childMeasures[i].height / 2);
					maxBottom = Math.max(maxBottom, childMeasures[i].height / 2);
				} else {
					maxTop = Math.max(maxTop, childMeasures[i].top);
					maxBottom = Math.max(maxBottom, childMeasures[i].bottom);
				}
			}
			const maxHeight = maxTop + maxBottom;
			return {
				width: width,
				height: maxHeight,
				maxTop,
				childMeasures,
				fontInfo,
			};
		},
	},
	digitcontainer: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				lineWidth,
				fontInfo,
				digitWidth,
				top,
				bottom,
				childMeasures,
			} = measure;
			context.save();
			context.lineWidth = lineWidth;
			const color = ColorUtil.getCurrentScheme();
			context.font = Font.fromInfo(fontInfo);
			let xStart = x;
			if (node.children.length) {
				node.children.forEach((d, i) => {
					const actualDigitWidth = childMeasures[i].width;
					if (d.type === "blank") {
						rc.pushBlank({
							type: d.blankType,
							x: xStart,
							y: y - top,
							width: digitWidth,
							height: top + bottom,
							typeInfo: {},
							answerSize: 1,
						});
					} else {
						// Need this transform to match the position of the blank
						// since blanks get transformed in rc.pushBlank
						// xStart uses value before transformation so not offset in instructions.
						const transformedRect = rc.transformRect([
							xStart,
							y - top,
							digitWidth,
							top + bottom,
						]);
						context
							.bp()
							.rect([
								xStart,
								transformedRect[1],
								transformedRect[2],
								transformedRect[3],
							])
							.stroke(color.digitOutline);
						rc.drawNode(rc, xStart + (digitWidth - actualDigitWidth) / 2, y, d);
					}
					xStart += digitWidth;
				});
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			// Everything must match size of blanks
			const blankFontInfo = _.extend({family: ["lizzie"]}, fontInfo);
			rc.context.save();
			rc.context.font = Font.fromInfo(blankFontInfo);
			const lineWidth = CanvasUtil.getOptimalLineWidth(rc.context);
			rc.context.restore();
			const xBuffer = 4 * lineWidth;
			const yBuffer = 2 * lineWidth;
			const numDigits = node.children.length;
			const digitWidth = getDigitWidth(rc, blankFontInfo) + 2 * xBuffer;

			let childMeasures;
			if (node.children.length) {
				childMeasures = node.children.map(c =>
					rc.measureNode(rc, fontInfo, props, c)
				);
			}

			return {
				width: digitWidth * numDigits,
				top: fontInfo.size / 2 + yBuffer,
				bottom: fontInfo.size / 2 + yBuffer,
				fontInfo,
				digitWidth,
				lineWidth,
				childMeasures,
			};
		},
	},
	boxdigits: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, top, bottom, digitWidth} = measure;
			const digits = node.digits;

			context.font = Font.fromInfo(fontInfo);
			let xStart = x;
			digits.forEach(d => {
				const actualDigitWidth = context.measureText(d).width;
				if (!node.noOutline) {
					context.strokeRect(xStart, y - top, digitWidth, top + bottom);
				}
				CanvasText.drawMiddleText(
					context,
					d,
					xStart + (digitWidth - actualDigitWidth) / 2,
					y
				);
				xStart += digitWidth;
			});
		},
		measure(rc, fontInfo, props, node) {
			const lineWidth = getLineWidth(rc, fontInfo);
			const xBuffer = 4 * lineWidth;
			const yBuffer = 2 * lineWidth;
			const digitWidth = getDigitWidth(rc, fontInfo) + 2 * xBuffer;

			const fontSize = fontInfo.size;
			const numDigits = node.digits.length;
			return {
				width: digitWidth * numDigits,
				top: fontSize / 2 + yBuffer,
				bottom: fontSize / 2 + yBuffer,
				fontInfo,
				digitWidth,
			};
		},
	},
	cancel: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width, cancelSize, cancelFontInfo, mainSize} = measure;
			const cancelType = node.cancelType || null;
			const isCenter = node.center;
			const lineWidth = getLineWidth(rc, fontInfo);
			const isLeft = cancelType === "lt" || cancelType === "lb";
			const isTop = cancelType === "lt" || cancelType === "rt";

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;

			// Draw child node first so it is under the cancel line(s)
			rc.drawNode(rc, x, y, node.children[0]);

			// Draw the line that cancels
			context.beginPath();
			if (!isLeft) {
				context.moveTo(x + width, y - mainSize.top);
				context.lineTo(x, y + mainSize.bottom);
			}
			if (cancelType === "x" || isLeft) {
				context.moveTo(x, y - mainSize.top);
				context.lineTo(x + width, y + mainSize.bottom);
			}
			context.stroke();

			// Draw the resulting number from cancel
			context.font = Font.fromInfo(cancelFontInfo);
			if (isCenter && isTop) {
				rc.drawNode(
					rc,
					x + (width - cancelSize.width) / 2,
					y - mainSize.top - cancelSize.bottom,
					node.children[1]
				);
			} else if (isCenter) {
				rc.drawNode(
					rc,
					x + (width - cancelSize.width) / 2,
					y + mainSize.bottom + cancelSize.top,
					node.children[1]
				);
			} else if (cancelType === "lt") {
				rc.drawNode(
					rc,
					x - cancelSize.width,
					y - mainSize.top - cancelSize.bottom,
					node.children[1]
				);
			} else if (cancelType === "lb") {
				rc.drawNode(
					rc,
					x + width,
					y + mainSize.bottom + cancelSize.top,
					node.children[1]
				);
			} else if (cancelType === "rt") {
				rc.drawNode(
					rc,
					x + width,
					y - mainSize.top - cancelSize.bottom,
					node.children[1]
				);
			} else if (cancelType === "rb") {
				rc.drawNode(
					rc,
					x - cancelSize.width,
					y + mainSize.bottom + cancelSize.top,
					node.children[1]
				);
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const mainSize = rc.measureNode(rc, fontInfo, props, node.children[0]);
			if (node.cancelType === "basic" || node.cancelType === "x") {
				return {
					top: mainSize.top,
					bottom: mainSize.bottom,
					width: mainSize.width,
					mainSize,
					fontInfo,
				};
			} else {
				const cancelRatio = node.noShrink ? 1 : 0.7;
				const cancelFontInfo = scaleFont(fontInfo, cancelRatio);
				const cancelSize = rc.measureNode(
					rc,
					cancelFontInfo,
					props,
					node.children[1]
				);
				const cancelHeight = cancelSize.top + cancelSize.bottom;
				let top = mainSize.top;
				let bottom = mainSize.bottom;
				if (node.cancelType === "lt" || node.cancelType === "rt") {
					top += cancelHeight;
				} else if (node.cancelType === "lb" || node.cancelType === "rb") {
					bottom += cancelHeight;
				}
				return {
					// Only count main size width but actually drawing outside this width
					// We don't want the canceled number to be part of the measure width.
					width: mainSize.width,
					top,
					bottom,
					fontInfo,
					cancelFontInfo,
					mainSize,
					cancelSize,
				};
			}
		},
	},
	carry: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, carryFontInfo, mainSize, carrySize} = measure;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			rc.drawNode(rc, x, y, node.children[0]);
			context.font = Font.fromInfo(carryFontInfo);
			const carryX = x + (mainSize.width - carrySize.width) / 2;
			const carryY = y - mainSize.top - carrySize.bottom;
			rc.drawNode(rc, carryX, carryY, node.children[1]);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const mainSize = rc.measureNode(rc, fontInfo, props, node.children[0]);
			const carryRatio = 0.7;
			const carryFontInfo = scaleFont(fontInfo, carryRatio);
			const carrySize = rc.measureNode(
				rc,
				carryFontInfo,
				props,
				node.children[1]
			);
			return {
				width: mainSize.width,
				top: mainSize.top + carrySize.bottom + carrySize.top,
				bottom: mainSize.bottom,
				fontInfo,
				carryFontInfo,
				mainSize: mainSize,
				carrySize: carrySize,
				carryRatio: carryRatio,
			};
		},
	},
	decimalMovement: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				punctWidth,
				digitWidth,
				bottom,
				fontInfo,
				labelFontInfo,
				spaceWidth,
				punctSpaceWidth,
				margin,
			} = measure;
			const fontSize = fontInfo.size;
			const labelFontSize = labelFontInfo.size;
			const digits = node.digits;
			const periodIndex = _.indexOf(digits, ".");
			const fromRight =
				node.fromRight > 0 && digits.length - node.fromRight - 2;
			let label = digits.length - periodIndex - 1;
			if (node.fromRight) label = node.fromRight;

			const arrowHeight = fontSize / 2;
			const arrowTopY = y + bottom - labelFontSize - arrowHeight + margin * 1.5;

			context.save();
			context.lineWidth = getLineWidth(rc, fontInfo);
			context.font = Font.fromInfo(fontInfo);
			let arrowPosition = 0;
			let xStart = x;
			//draw digits and curve underneath
			digits.forEach((d, index) => {
				const space = /\d/.test(d) ? spaceWidth : punctSpaceWidth;
				const currentDigitWidth = /\d/.test(d) ? digitWidth : punctWidth;
				if (index === periodIndex) {
					arrowPosition = xStart + space;
				}
				if ((!node.fromRight && index === periodIndex) || index === fromRight) {
					context.bp().M(xStart + space, arrowTopY);
				}
				CanvasText.drawMiddleText(
					context,
					d,
					xStart + space / 2 - currentDigitWidth / 2,
					y
				);
				if (
					(index > periodIndex && !node.fromRight) ||
					(index > fromRight && index !== periodIndex)
				) {
					context.quadraticCurveTo(
						xStart + space / 2,
						arrowTopY + arrowHeight,
						xStart + space,
						arrowTopY
					);
				}
				xStart += space;
			});
			context.stroke();

			//draws label and arrowhead
			if (label > 0) {
				context.font = Font.fromInfo(labelFontInfo);
				const labelWidth =
					label.toString().length * getDigitWidth(rc, labelFontInfo);
				let labelX = arrowPosition + (label * spaceWidth) / 2;
				if (node.fromRight)
					labelX = arrowPosition - ((label + 1) * spaceWidth) / 2;
				CanvasText.drawMiddleText(
					context,
					label,
					labelX - labelWidth / 2,
					y + bottom - labelFontSize / 2
				);
				if (node.arrow) {
					const headLength = arrowHeight / 2.5;
					const headHeight = (headLength * 2) / 3;
					const startX = node.fromRight
						? xStart - spaceWidth
						: arrowPosition + spaceWidth / 2;
					const endX = node.fromRight ? xStart - spaceWidth / 2 : arrowPosition;
					const arrowPositions = calculateArrowHeadPositions({
						startX,
						endX,
						startY: arrowTopY + arrowHeight * 0.8,
						endY: arrowTopY,
						headLength,
						headHeight,
					});
					drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
				}
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const digitWidth = getDigitWidth(rc, fontInfo);
			const fontSize = fontInfo.size;
			const numDigits = node.digits.filter(d => /\d/.test(d)).length;
			const numPuncts = node.digits.length - numDigits;
			const padding = digitWidth / 16;
			const margin = digitWidth / 4;
			const spaceWidth = digitWidth + padding;
			const punctSpaceWidth = spaceWidth / 2;
			const labelFontInfo = scaleFont(fontInfo, 0.7);
			rc.context.save();
			rc.context.font = Font.fromInfo(fontInfo);
			const punctWidth = rc.context.measureText(".").width;
			rc.context.restore();
			return {
				width: spaceWidth * numDigits + punctSpaceWidth * numPuncts,
				top: fontSize / 2,
				bottom: fontSize + labelFontInfo.size - margin * 2,
				margin,
				digitWidth,
				fontInfo,
				labelFontInfo,
				spaceWidth,
				punctSpaceWidth,
				punctWidth,
			};
		},
	},
	decimalArrowDiagram: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				powerOfTenWidths,
				digitWidth,
				xRight,
				blankWidth,
				yLine,
				margin,
				arrowHeight,
				addSpace,
				spaceWidth,
				periodSpaceWidth,
				equalWidth,
				multWidth,
				timesWidth,
				powerFontInfo,
				fontInfo,
			} = measure;
			const fontSize = fontInfo.size;
			const lineWidth = getLineWidth(rc, fontInfo);
			const {periodIndices, power, digits} = node;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			rc.drawNode(
				rc,
				x + spaceWidth / 2 - equalWidth / 2,
				y + yLine[1],
				node.children[3]
			);
			rc.drawNode(
				rc,
				x + xRight + spaceWidth * 1.5 - timesWidth / 2,
				y + yLine[0],
				node.children[0]
			);
			rc.drawNode(
				rc,
				x + xRight + spaceWidth * 2.5 + Math.max(-multWidth / 2, -digitWidth),
				y + yLine[0],
				node.children[2]
			);

			const arrowTopY = y + yLine[1] + fontSize / 2 + 2 * margin;

			//draw digits and underlines

			//will contain x coordinate of center of each digit (including period)
			const xPositions = [[], []];
			for (let line = 0; line < 2; line++) {
				let xStart = x + spaceWidth + addSpace[line];
				digits[line].forEach((d, index) => {
					const space =
						index === periodIndices[line] ? periodSpaceWidth : spaceWidth;
					const xPos = xStart + space / 2;
					xPositions[line].push(xPos);
					if (index !== periodIndices[line]) {
						if (d !== " ") {
							context
								.bp()
								.M(
									xPos - blankWidth / 2,
									y + fontSize / 2 + margin + yLine[line]
								)
								.l(blankWidth, 0)
								.stroke();
							CanvasText.drawMiddleText(
								context,
								d,
								xPos - digitWidth / 2,
								y + yLine[line]
							);
						}
					} else {
						context
							.bp()
							.arc(
								xPos,
								y + fontSize / 2 + margin + yLine[line],
								1.8 * lineWidth,
								0,
								2 * Math.PI
							)
							.fill();
					}
					xStart += space;
				});
			}

			if (!node.lineArrows && power !== 0) {
				//draw curvy movement arrow
				const start =
					power > 0 ? periodIndices[1] - power : periodIndices[1] + 1;
				const end = power > 0 ? periodIndices[1] : periodIndices[1] - power + 1;
				context.bp().M(xPositions[1][start] - spaceWidth / 2, arrowTopY);
				for (let pos = start; pos < end; pos++) {
					context.quadraticCurveTo(
						xPositions[1][pos],
						arrowTopY + arrowHeight,
						xPositions[1][pos] + spaceWidth / 2,
						arrowTopY
					);
				}
				context.stroke();
				const headLength = arrowHeight / 2;
				const headHeight = (headLength * 2) / 3;
				const arrowPositions = calculateArrowHeadPositions({
					startX:
						xPositions[1][periodIndices[1]] +
						((power > 0 ? -1 : 1) * (periodSpaceWidth + spaceWidth)) / 2,
					endX:
						xPositions[1][periodIndices[1]] +
						((power > 0 ? -1 : 1) * periodSpaceWidth) / 2,
					startY: arrowTopY + arrowHeight * 0.8,
					endY: arrowTopY,
					headLength,
					headHeight,
				});
				drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
			}

			if (node.lineArrows) {
				//draw power of ten labels
				context.save();
				context.font = Font.fromInfo(powerFontInfo);
				const max = periodIndices[0] > periodIndices[1] ? 0 : 1;
				const maxPeriodIndex = Math.max(periodIndices[0], periodIndices[1]);
				const powersNodes = node.children[1].children;
				for (let i = 0; i < powersNodes.length; i++) {
					const pos = i >= maxPeriodIndex ? i + 1 : i;
					const xPos =
						pos < digits[max].length
							? xPositions[max][pos]
							: xPositions[1 - max][
									pos + periodIndices[1 - max] - periodIndices[max]
							  ];
					rc.drawNode(rc, xPos - powerOfTenWidths[i] / 2, y, powersNodes[i]);
					CanvasText.drawMiddleText(
						context,
						"'s",
						xPos + powerOfTenWidths[i] / 2,
						y
					);
				}
				context.restore();

				//draw arrows
				const yStart = y + yLine[0] + fontSize / 2 + 2 * margin;
				const yEnd = y + yLine[0] + fontSize / 2 + arrowHeight + margin;
				for (
					let xIndexStart = 0;
					xIndexStart < digits[0].length;
					xIndexStart++
				) {
					if (xIndexStart !== periodIndices[0]) {
						let xIndexEnd =
							xIndexStart - periodIndices[0] - power + periodIndices[1];
						if (
							xIndexStart > periodIndices[0] &&
							xIndexEnd <= periodIndices[1]
						) {
							xIndexEnd--;
						} else if (
							xIndexStart < periodIndices[0] &&
							xIndexEnd >= periodIndices[1]
						) {
							xIndexEnd++;
						}
						if (0 <= xIndexEnd && xIndexEnd < digits[1].length) {
							const xStart =
								xPositions[0][xIndexStart] +
								((power > 0 ? -1 : 1) * spaceWidth) / 6;
							const xEnd =
								xPositions[1][xIndexEnd] +
								((power > 0 ? 1 : -1) * spaceWidth) / 4;
							context
								.bp()
								.M(xStart, yStart)
								.L(xEnd, yEnd)
								.stroke();
							const headLength = arrowHeight / 2;
							const headHeight = (headLength * 2) / 3;
							const arrowPositions = calculateArrowHeadPositions({
								startX: xStart,
								endX: xEnd,
								startY: yStart,
								endY: yEnd,
								headLength,
								headHeight,
							});
							drawArrowHead(context, _.extend(arrowPositions, {curved: true}));
						}
					}
				}
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const digitWidth = getDigitWidth(rc, fontInfo);
			const lineWidth = getLineWidth(rc, fontInfo);
			const fontSize = fontInfo.size;
			const margin = digitWidth / 4;
			const digits = node.digits;
			const spaceWidth = digitWidth + 8 * margin;
			const blankWidth = digitWidth + margin * 6.25;
			const periodSpaceWidth = spaceWidth / 3;
			const powerFontInfo = scaleFont(fontInfo, 0.5);
			const timesWidth = rc.measureNode(rc, fontInfo, props, node.children[0])
				.width;
			const multWidth = rc.measureNode(rc, fontInfo, props, node.children[2])
				.width;
			const equalWidth = rc.measureNode(rc, fontInfo, props, node.children[3])
				.width;
			const powersOfTen = node.children[1].children;
			let maxPowerTop = 0;
			let maxPowerBottom = 0;
			const powerOfTenWidths = [];
			powersOfTen.forEach(p => {
				const powerMeasurement = rc.measureNode(rc, powerFontInfo, props, p);
				powerOfTenWidths.push(powerMeasurement.width);
				maxPowerTop = Math.max(powerMeasurement.top, maxPowerTop);
				maxPowerBottom = Math.max(powerMeasurement.bottom, maxPowerBottom);
			});
			const arrowHeight = 1.4 * (fontSize / 2 + margin);
			const periodIndices = node.periodIndices;
			const addPos = [
				Math.max(periodIndices[1] - periodIndices[0], 0),
				Math.max(periodIndices[0] - periodIndices[1], 0),
			];
			const addSpace = node.lineArrows
				? [addPos[0] * spaceWidth, addPos[1] * spaceWidth]
				: [0, 0];
			const xRight = Math.max(
				addSpace[0] + (digits[0].length - 1) * spaceWidth + periodSpaceWidth,
				addSpace[1] + (digits[1].length - 1) * spaceWidth + periodSpaceWidth
			);
			const yLine0 = node.lineArrows
				? maxPowerBottom + arrowHeight / 2 + fontSize / 2
				: 0;
			const yLine1 = yLine0 + fontSize + margin + arrowHeight + lineWidth / 2;
			const yLine1Bottom = !node.lineArrows
				? arrowHeight / 2 + margin
				: 1.8 * lineWidth;
			return {
				width:
					2.5 * spaceWidth +
					xRight +
					Math.max(multWidth / 2, multWidth - digitWidth),
				top: node.lineArrows ? maxPowerTop : fontSize / 2,
				bottom: yLine1 + fontSize / 2 + margin + lineWidth + yLine1Bottom,
				timesWidth,
				multWidth,
				equalWidth,
				addSpace,
				fontInfo,
				spaceWidth,
				periodSpaceWidth,
				xRight,
				arrowHeight,
				yLine: [yLine0, yLine1],
				margin,
				blankWidth,
				powerFontInfo,
				digitWidth,
				powerOfTenWidths,
			};
		},
	},
	absoluteValue: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width, top, bottom} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);
			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			context.beginPath();
			context.moveTo(x, y - top);
			context.lineTo(x, y + bottom);
			context.moveTo(x + width, y - top);
			context.lineTo(x + width, y + bottom);
			context.stroke();
			context.restore();
			rc.drawNode(rc, x + 3 * lineWidth, y, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const innerMeasured = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			const lineWidth = getLineWidth(rc, fontInfo);
			const yPadding = fontInfo.size / 12;
			return {
				width: innerMeasured.width + 4 * lineWidth,
				top: innerMeasured.top + yPadding,
				bottom: innerMeasured.bottom + yPadding,
				fontInfo,
				xPadding: fontInfo.size / 12,
				// extra
				yPadding,
			};
		},
	},
	parentheses: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width, parenScale, parenWidth} = measure;
			context.save();
			context.scale(1, parenScale);
			context.font = Font.fromInfo(fontInfo);
			CanvasText.drawMiddleText(context, "(", x, y / parenScale);
			CanvasText.drawMiddleText(
				context,
				")",
				x + width - parenWidth,
				y / parenScale
			);
			context.restore();
			rc.drawNode(rc, x + parenWidth, y, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const bottomPadding = fontInfo.size / 5;
			const innerMeasured = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			const parenScale =
				(innerMeasured.top + innerMeasured.bottom) / fontInfo.size;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			const parenWidth = context.measureText(")").width;
			context.restore();
			return {
				width: innerMeasured.width + 2 * parenWidth,
				top: innerMeasured.top,
				bottom: innerMeasured.bottom + bottomPadding,
				fontInfo,
				// extra
				parenWidth,
				parenScale,
			};
		},
	},
	numberAverage: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, measure.fontInfo);
			const fontSize = measure.fontInfo.size;
			const smallerFontSize = measure.smallerFontInfo.size;
			const shiftWidth = measure.shiftMeasure.width;
			const {
				diffMeasure,
				givenNumsMeasure,
				labelsMeasure,
				braceRadius,
				vertSpacing,
			} = measure;

			//inputs
			const givenNums = node.givenNumbers;
			const differences = node.differences;
			const shift = node.shift;
			const labels = node.labels;
			const hasNoBrace = node.hasNoBrace;

			//positioning variables for the nodes
			const firstColMeasure = measure.colMeasures[0] / 2;
			let currentX = x;
			const shiftY = y;
			let differencesX = currentX + firstColMeasure - diffMeasure[0].width / 2;
			const differencesY = y + (hasNoBrace ? 0 : fontSize + 2 * braceRadius);
			let givenNumbersX =
				currentX + firstColMeasure - givenNumsMeasure[0].width / 2;
			const givenNumbersY = differencesY + fontSize + vertSpacing;
			let labelsX = currentX;
			const labelsY = givenNumbersY + fontSize + vertSpacing;

			//draw the differences, givenNumbers, and labels (if given) column-by-column
			for (let i = 0; i < givenNums.length; i++) {
				rc.drawNode(rc, givenNumbersX, givenNumbersY, givenNums[i]);

				//change font size and draw difference and label for current column
				context.save();
				context.font = Font.fromInfo(measure.smallerFontInfo);
				rc.drawNode(rc, differencesX, differencesY, differences[i]);

				if (labelsMeasure.length > 0) {
					if (i === 0) {
						labelsX = currentX + firstColMeasure - labelsMeasure[0].width / 2;
					}
					rc.drawNode(rc, labelsX, labelsY, labels[i]);
				}

				context.restore();

				//update drawing positions
				currentX += measure.colMeasures[i] + measure.delta;
				if (i < givenNums.length - 1) {
					const nextColMeasure = measure.colMeasures[i + 1] / 2;
					differencesX =
						currentX + nextColMeasure - diffMeasure[i + 1].width / 2;
					givenNumbersX =
						currentX + nextColMeasure - givenNumsMeasure[i + 1].width / 2;
					if (labelsMeasure.length > 0) {
						labelsX =
							currentX + nextColMeasure - labelsMeasure[i + 1].width / 2;
					}
				}
			}

			//Draw the total shift
			if (!hasNoBrace) {
				const shiftX = (x + currentX - measure.delta - shiftWidth) / 2;
				rc.drawNode(rc, shiftX, shiftY, shift, measure);

				//Draw the brace below the shift
				const braceStartX = x;
				const braceStartY = differencesY + 0.4 * smallerFontSize;

				context.save();
				context.lineWidth = lineWidth;
				context.bp().M(braceStartX, braceStartY - smallerFontSize);
				context.arc(
					braceStartX + braceRadius,
					braceStartY - smallerFontSize,
					braceRadius,
					Math.PI,
					(3 / 2) * Math.PI
				);
				context.L(
					shiftX + shiftWidth / 2 - braceRadius,
					braceStartY - braceRadius - smallerFontSize
				);
				context.arc(
					shiftX + shiftWidth / 2 - braceRadius,
					braceStartY - 2 * braceRadius - smallerFontSize,
					braceRadius,
					Math.PI / 2,
					0,
					true
				);
				context.arc(
					shiftX + shiftWidth / 2 + braceRadius,
					braceStartY - 2 * braceRadius - smallerFontSize,
					braceRadius,
					Math.PI,
					Math.PI / 2,
					true
				);
				context.L(
					currentX - measure.delta,
					braceStartY - smallerFontSize - braceRadius
				);
				context
					.arc(
						currentX - measure.delta,
						braceStartY - smallerFontSize,
						braceRadius,
						Math.PI * (3 / 2),
						Math.PI * 2
					)
					.stroke()
					.Z();
				context.restore();
			}
		},
		measure(rc, fontInfo, props, node) {
			const fontSize = fontInfo.size;
			const smallerFontInfo = scaleFont(fontInfo, 5 / 7);
			const smallerFontSize = smallerFontInfo.size;
			const braceRadius = (1 / 4) * fontSize;
			const vertSpacing = 0.1 * fontSize;
			//width variables
			let objWidth = 0;
			const delta = (node.labels.length ? 1.5 : 1) * fontSize; //spacing between columns

			const givenNumsMeasure = [];
			const diffMeasure = [];
			const shiftMeasure = rc.measureNode(rc, fontInfo, props, node.shift);
			const labelsMeasure = [];

			//Find the measures of each node
			const colMeasures = [];
			for (let i = 0; i < node.givenNumbers.length; i++) {
				givenNumsMeasure.push(
					rc.measureNode(rc, fontInfo, props, node.givenNumbers[i])
				);
				diffMeasure.push(
					rc.measureNode(rc, smallerFontInfo, props, node.differences[i])
				);

				//find measure of each column
				let currentColMeasure = Math.max(
					givenNumsMeasure[i].width,
					diffMeasure[i].width
				);
				if (node.labels.length > 0) {
					labelsMeasure.push(
						rc.measureNode(rc, smallerFontInfo, props, node.labels[i])
					);
					currentColMeasure = Math.max(
						currentColMeasure,
						labelsMeasure[i].width
					);
				}
				colMeasures.push(currentColMeasure);

				//update object width
				objWidth += currentColMeasure + delta;
			}

			//final object width
			// account for braceRadius here due to the extra width gained by the curvature of the brace, which has radius braceRadius
			objWidth = objWidth - delta + braceRadius;

			//Set height of the whole object based on brace presence and blanks
			//brace arc radius + gap between brace and shift + shift height
			const braceHeight = braceRadius + smallerFontSize + fontSize;
			let objHeight = fontSize + smallerFontSize;

			if (!node.hasNoBrace) {
				objHeight += braceHeight + vertSpacing;
			}

			if (labelsMeasure.length > 0) {
				objHeight += smallerFontSize + vertSpacing;
			}

			return {
				width: objWidth,
				top: 0,
				bottom: objHeight,
				fontInfo,
				smallerFontInfo,
				givenNumsMeasure,
				diffMeasure,
				shiftMeasure,
				labelsMeasure,
				colMeasures,
				delta,
				braceRadius,
				vertSpacing,
			};
		},
	},
	baseBlank: {
		draw(rc, x, y, node, measure) {
			const {children, labelTypes} = node;
			const {
				top,
				singleWidth,
				diagMeasures,
				expMeasures,
				digMeasures,
				diagExtra,
				padding,
				lineWidth,
				diagFontInfo,
			} = measure;
			const ctx = rc.context;
			let xStart = x + padding + diagExtra;
			_.each(children[0].listData, (dig, i) => {
				// Drawing decimal and commas
				if (_.includes([".", ","], dig.text)) {
					rc.drawNode(rc, xStart, y + (digMeasures[i].top * 2) / 3, dig);
					xStart += padding + digMeasures[i].width;
				} else {
					// Drawing the node on top of the line
					if (dig.text !== "y") {
						rc.drawNode(
							rc,
							xStart + (singleWidth - digMeasures[i].width) / 2,
							y,
							dig
						);
					}
					let expHeight = 0;
					// Drawing exponents under the line
					if (labelTypes.exps) {
						expHeight = expMeasures[i].top + expMeasures[i].bottom + padding;
						rc.drawNode(
							rc,
							xStart + (singleWidth - expMeasures[i].width) / 2,
							y + top + 2 * padding + expMeasures[i].top,
							children[1].listData[i]
						);
					}
					ctx.save();
					ctx.lineWidth = lineWidth;
					ctx
						.bp()
						.M(xStart, y + top + padding)
						.h(singleWidth)
						.stroke();
					ctx.translate(
						xStart + singleWidth / 1.5,
						y + top + padding + expHeight + diagFontInfo.size / 3
					);
					ctx.rotate(Math.PI / -4);
					if (labelTypes.diag) {
						const xText = -1 * diagMeasures[i].width;
						rc.drawNode(rc, xText, 0, children[2].listData[i]);
					}
					ctx.restore();
					xStart += singleWidth + padding;
				}
			});
		},
		measure(rc, fontInfo, props, node) {
			const {children, labelTypes, hasDecimal} = node;
			const padding = fontInfo.size / 4;
			const lineWidth = getLineWidth(rc, fontInfo);
			const puncFontInfo = scaleFont(fontInfo, 1.25);
			const digMeasures = _.map(children[0].listData, e => {
				const font = _.includes([".", ","], e.text) ? puncFontInfo : fontInfo;
				return rc.measureNode(rc, font, props, e);
			});
			const digHeight = _.max(_.map(digMeasures, e => e.top + e.bottom));
			const digWidth = _.max(_.map(digMeasures, e => e.width));
			const diagFontInfo =
				labelTypes.nums && hasDecimal ? scaleFont(fontInfo, 2 / 3) : fontInfo;
			const diagMeasures = _.map(children[2].listData, e =>
				rc.measureNode(rc, diagFontInfo, props, e)
			);
			const expMeasures = _.map(children[1].listData, e =>
				rc.measureNode(rc, fontInfo, props, e)
			);
			const expWidth = labelTypes.exps
				? _.max(_.map(expMeasures, e => e.width))
				: 0;
			const expHeight = labelTypes.exps
				? _.max(_.map(expMeasures, e => e.top + e.bottom))
				: 0;
			const maxDiagHeight = labelTypes.diag
				? _.max(_.map(diagMeasures, l => l.top + l.bottom))
				: 0;
			const maxDiagFull = labelTypes.diag
				? _.max(_.map(diagMeasures, l => l.top + l.bottom + l.width))
				: 0;
			const singleWidth =
				padding +
				Math.max(
					digWidth,
					maxDiagHeight / Math.sqrt(2),
					expWidth,
					fontInfo.size * 1.25
				);
			let diagExtra = 0;
			if (labelTypes.diag) {
				diagExtra =
					singleWidth / -3 +
					_.max(_.map(diagMeasures, (e, i) => e.width - i * singleWidth)) /
						Math.sqrt(2);
			}
			let width = diagExtra + padding;
			_.each(children[0].listData, (elem, i) => {
				width +=
					(_.includes([".", ","], elem.text)
						? digMeasures[i].width
						: singleWidth) + padding;
			});
			const top = digHeight / 2;
			// Consider the bottom of the digit to the line
			let bottom = digHeight / 2 + padding + lineWidth;
			// Then exponents, or padding, if there are none.
			bottom += labelTypes.exps ? expHeight + 2 * padding : padding;
			// Then consider the height of the diagonal labels (if there are none, maxDiagFull is just 0)
			bottom += maxDiagFull / Math.sqrt(2);
			return {
				width,
				top,
				bottom,
				singleWidth,
				lineWidth,
				digMeasures,
				expMeasures,
				diagMeasures,
				diagExtra,
				diagFontInfo,
				padding,
			};
		},
	},
	stack: {
		draw(rc, x, y, node, measure) {
			const {top, fontInfo, colCount} = measure;
			const cm = measure.childMeasure.base;
			const {children} = node;
			const topLeft = {
				x: x,
				y: y - top,
			};
			const ctx = rc.context;

			// draw positions
			children.forEach((row, rowIndex) => {
				row.children.forEach((childNode, index, rowData) => {
					const xPosition = index + (colCount - row.children.length);
					const actualMeasure = measure.childMeasure.positions[rowIndex][index];

					const childX =
						topLeft.x +
						cm.xPadding +
						(cm.width + cm.xPadding) * xPosition +
						actualMeasure.centeringOffset;
					const childY =
						topLeft.y +
						cm.yPadding +
						cm.top +
						(cm.top + cm.bottom + cm.yPadding) * rowIndex;

					if (childNode.type === "blank") {
						rc.pushBlank({
							type: "simple",
							x: childX,
							y: childY - cm.top,
							width:
								childNode.answerSize === 2
									? cm.width
									: cm.singleDigitBlankWidth,
							height: cm.top + cm.bottom,
							typeInfo: {},
							answerSize: childNode.answerSize,
						});
					} else {
						if (
							_.includes(node.noCommaRows, rowIndex) &&
							childNode.type !== "fakeblank"
						) {
							actualMeasure.fontInfo.size = scaleFont(fontInfo, 0.8).size;
						}
						rc.drawNode(rc, childX, childY, childNode, actualMeasure);
					}

					let finalChildNode = childNode;

					while (finalChildNode.children && finalChildNode.children.length) {
						finalChildNode = finalChildNode.children[0];
					}

					if (finalChildNode.cancel) {
						const baseX =
							topLeft.x + (cm.width + cm.xPadding) * xPosition + cm.xPadding;
						const baseY = childY;
						const xPadding = cm.width * 0.12;
						const topPadding = cm.top * 0.28;
						const bottomPadding = cm.bottom * 0.28;
						ctx.save();
						ctx.globalAlpha = 0.5;
						ctx.lineWidth = fontInfo.size * 0.1;
						ctx
							.bp()
							.M(baseX + xPadding, baseY - cm.top + topPadding)
							.L(baseX + cm.width - xPadding, baseY + cm.bottom - bottomPadding)
							.Z();
						ctx.stroke();
						ctx
							.bp()
							.M(baseX + xPadding, baseY + cm.bottom - bottomPadding)
							.L(baseX + cm.width - xPadding, baseY - cm.top + topPadding)
							.Z();
						ctx.stroke();
						ctx.restore();
					}
				});
			});

			// draw operator
			const opMeasure = measure.childMeasure.operator;
			const opX = topLeft.x + (cm.width - opMeasure.width) / 2;
			const opY =
				topLeft.y +
				cm.top +
				(cm.top + cm.bottom + cm.yPadding) * (children.length - 2) +
				cm.yPadding;
			rc.drawNode(
				rc,
				opX,
				opY,
				{
					type: "operator",
					text: node.operator,
					isUnary: false,
				},
				opMeasure
			);

			// draw horizontal line
			const lineY =
				topLeft.y +
				cm.yPadding +
				(cm.top + cm.bottom + cm.yPadding) * (children.length - 1) -
				cm.yPadding / 2;
			ctx.save();
			ctx.beginPath();
			ctx.M(topLeft.x, lineY);
			ctx.L(topLeft.x + measure.width, lineY);
			ctx.closePath();
			ctx.stroke();
			ctx.restore();

			const getGapXAfterCell = (row, afterIndex) => {
				return (
					topLeft.x +
					measure.width -
					(row.children.length - afterIndex - 1) * (cm.width + cm.xPadding)
				);
			};

			const getRowY = rowIndex =>
				topLeft.y +
				cm.top +
				cm.yPadding +
				(cm.top + cm.bottom + cm.yPadding) * rowIndex;

			// draw decimal points
			if (node.decimalPlaces > 0) {
				children.forEach((row, rowIndex) => {
					if (node.noCommaRows && _.includes(node.noCommaRows, rowIndex)) {
						return;
					}
					const len = row.children.length;
					const decimalAfterIndex = len - node.decimalPlaces - 1;
					const gapAfterIndex = decimalAfterIndex >= 0 ? decimalAfterIndex : -1;
					const decimal = {type: "text", text: "."};
					rc.drawNode(
						rc,
						getGapXAfterCell(row, gapAfterIndex),
						getRowY(rowIndex),
						decimal,
						measure.childMeasure.decimal
					);
				});
			}

			// draw commas
			children.forEach((row, rowIndex) => {
				if (!node.noCommaRows || !_.includes(node.noCommaRows, rowIndex)) {
					const integerCellCount =
						node.decimalPlaces > 0
							? row.children.length - node.decimalPlaces
							: row.children.length;

					let positionsLeft = integerCellCount;
					while (positionsLeft > 3) {
						if (positionsLeft % 3 === 1) {
							// draw a comma
							const commaAfterIndex = integerCellCount - positionsLeft;
							const comma = {type: "text", text: ","};
							rc.drawNode(
								rc,
								getGapXAfterCell(row, commaAfterIndex),
								getRowY(rowIndex),
								comma,
								measure.childMeasure.comma
							);
						}
						positionsLeft--;
					}
				}
			});
		},
		measure(rc, fontInfo, props, node) {
			const scaledFont = node.big ? Helpers.scaleFont(fontInfo, 1.5) : fontInfo;
			const fontSize = scaledFont.size;
			const m = {
				width: 1.34 * fontSize,
				singleDigitBlankWidth: 1.04 * fontSize,
				top: 0.63 * fontSize,
				bottom: 0.63 * fontSize,
			};

			const rowCount = node.children.length;
			const colCount = _.max(
				_.map(node.children, (row, index, rows) => {
					// the operator goes to the left of any content above the horizontal line,
					// but the total line on the bottom can have more places
					return index === rows.length - 1
						? row.children.length
						: row.children.length + 1;
				})
			);

			const childMeasures = _.map(node.children, (row, rowIndex) => {
				const rowMeasures = [];
				row.children.forEach(position => {
					const actualMeasure = rc.measureNode(rc, scaledFont, props, position);
					let lastChild = position;
					while (
						lastChild &&
						lastChild.children &&
						lastChild.children.length > 0
					) {
						lastChild = lastChild.children[0];
					}

					if (lastChild.type === "fakeblank") {
						if (lastChild.answerSize === 2) {
							actualMeasure.width = m.width * 1.1;
						} else {
							actualMeasure.width = m.singleDigitBlankWidth * 1.2;
						}
					}
					let centeringOffset = 0;
					if (lastChild.type !== "blank") {
						centeringOffset = (m.width - actualMeasure.width) / 2;
					} else {
						if (lastChild.answerSize < 2) {
							centeringOffset = (m.width - m.singleDigitBlankWidth) / 2;
						} else {
							actualMeasure.width = m.width * 1.1;
							centeringOffset = -0.1 * m.width;
						}
					}
					actualMeasure.centeringOffset = centeringOffset;
					rowMeasures.push(actualMeasure);
				});
				return rowMeasures;
			});

			const xPadding = fontSize * 0.2;
			const yPadding = fontSize * 0.4;

			return {
				width: (m.width + xPadding) * colCount,
				top: (m.top + yPadding / 2) * rowCount + m.top / 2,
				bottom: (m.bottom + yPadding / 2) * rowCount + m.bottom / 2,
				childMeasure: {
					base: _.extend(m, {xPadding, yPadding}),
					positions: childMeasures,
					operator: rc.measureNode(rc, scaledFont, props, {
						type: "operator",
						text: node.operator,
						isUnary: false,
					}),
					comma: rc.measureNode(rc, scaledFont, props, {
						type: "text",
						text: ",",
					}),
					decimal: rc.measureNode(rc, scaledFont, props, {
						type: "text",
						text: ".",
					}),
				},
				colCount,
				fontInfo: scaledFont,
				props,
			};
		},
	},
	angle: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);

			const startX = x + width * 0.88;
			const startY =
				y + fontInfo.size * 0.3 - 0.85 * width * Math.sin(Math.PI / 3);
			const midX = x + width * 0.23;
			const midY = y + fontInfo.size * 0.3;

			context.save();
			context.lineWidth = lineWidth;
			context.bp().moveTo(startX, startY);
			context.lineTo(midX, midY).stroke();
			context.lineTo(startX, midY).stroke();
			context.Z();
			context.restore();
		},
		measure(rc, fontInfo, props) {
			return {
				width: fontInfo.size,
				top: fontInfo.size * 0.5,
				bottom: fontInfo.size * 0.5,
				fontInfo,
			};
		},
	},
	sqrt: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				fontInfo,
				width,
				top,
				bottom,
				rootWidth,
				rootHeight,
				hasChildren,
				rightPadding,
			} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			context.beginPath();
			const leftX =
				x +
				width -
				rightPadding -
				measure.inRootMeasured.width -
				2 * lineWidth -
				rootWidth;
			context.moveTo(x + width - rightPadding, y - top + (lineWidth * 3) / 2);
			context.lineTo(leftX + rootWidth, y - top + (lineWidth * 3) / 2);
			context.lineTo(leftX + rootWidth * (1 / 2), y + bottom - lineWidth);
			context.lineTo(leftX + rootWidth * (1 / 8), y + bottom - rootHeight);
			context.lineTo(leftX, y + bottom - rootHeight + rootWidth * (1 / 8));
			context.stroke();
			if (node.nthRoot) {
				context.font = Font.fromInfo(measure.nthRootFontInfo);
				context.textBaseline = "top";
				context.fillText(
					node.nthRoot,
					leftX + rootWidth - measure.nthRootOffset - measure.nthRootWidth,
					y + bottom - rootHeight - measure.nthRootHeight - 2 * lineWidth
				);
			}
			context.restore();
			if (hasChildren) {
				rc.drawNode(rc, leftX + rootWidth + lineWidth, y, node.children[0]);
			}
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, fontInfo);
			context.save();
			context.font = Font.fromInfo(fontInfo);
			let inRootMeasured;
			const hasChildren = node.children[0].children.length;
			if (hasChildren) {
				inRootMeasured = rc.measureNode(rc, fontInfo, props, node.children[0]);
			} else {
				inRootMeasured = {
					top: fontInfo.size / 2,
					bottom: fontInfo.size / 2,
					width: context.measureText("2").width,
				};
			}
			const rootWidth = (context.measureText("0").width * 3) / 2;
			const rootHeight = (inRootMeasured.top + inRootMeasured.bottom) / 2;
			const rightPadding = 0.5 * lineWidth;
			let nthRootWidth = 0;
			let nthRootHeight = 0;
			let widthExtra = rootWidth;
			let nthRootFontInfo;
			let nthRootOffset;
			if (node.nthRoot) {
				nthRootFontInfo = _.extend({}, fontInfo, {size: fontInfo.size / 2});
				context.font = Font.fromInfo(nthRootFontInfo);
				nthRootWidth = context.measureText(node.nthRoot).width;
				nthRootHeight = nthRootFontInfo.size;
				nthRootOffset = (rootWidth * 1) / 4 + 2 * lineWidth;
				widthExtra = Math.max(rootWidth, nthRootOffset + nthRootWidth);
			}
			context.restore();
			return {
				width: inRootMeasured.width + widthExtra + 2 * lineWidth + rightPadding,
				top: inRootMeasured.top + 4 * lineWidth,
				bottom: inRootMeasured.bottom,
				fontInfo,
				inRootMeasured,
				rootWidth,
				rootHeight,
				nthRootFontInfo,
				nthRootOffset,
				nthRootWidth,
				nthRootHeight,
				hasChildren,
				rightPadding,
			};
		},
	},
	coloredDice: {
		draw(rc, x, y, node, measure) {
			const ctx = rc.context;

			// uncomment to draw guidelines, useful for debugging
			// ctx.save();
			// ctx.beginPath();
			// ctx.strokeStyle = "grey";
			// // square as defined by measurements
			// ctx.strokeRect(x, y - measure.top, measure.width, measure.top * 2);
			// // imaginary circle created by a rotating die
			// ctx.arc(x + measure.sizeAdjusted / 2, y, Math.sqrt(2 * measure.size**2) / 2, 0, 2 * Math.PI);
			// ctx.stroke("blue");
			// ctx.closePath();
			// ctx.restore();

			ctx.save();
			ctx.font = Font.fromInfo(measure.fontInfo);
			const {size, sizeAdjusted} = measure;
			const {diceAngle: angle, diceValue: value, dotColor} = node;
			ctx.fillStyle = colors[node.fillColor];

			// applies rotation of dice, if needed
			ctx.translate(x + sizeAdjusted / 2, y);
			ctx.rotate((angle * Math.PI) / 180);
			ctx.translate(-1 * (x + size / 2), -1 * y);

			// draw rounded rectangle
			DrawRectUtil.drawRoundedRect(ctx, x, y - size / 2, size, size);

			// fill and outline die
			ctx.fill();
			ctx.stroke();

			// define dot locations
			const dots = {};
			const padding = 0.25;
			let xSize = padding * size;
			let ySize = padding * size;
			dots.tl = {xSize, ySize}; // top-left
			ySize = size * 0.5;
			dots.ml = {xSize, ySize}; // middle-left
			ySize = size * (1 - padding);
			dots.bl = {xSize, ySize}; // bottom-left
			xSize = size * 0.5;
			ySize = size * 0.5;
			dots.m = {xSize, ySize}; // middle
			xSize = size * (1 - padding);
			ySize = padding * size;
			dots.tr = {xSize, ySize}; // top-right
			ySize = size * 0.5;
			dots.mr = {xSize, ySize}; // middle-right
			ySize = size * (1 - padding);
			dots.br = {xSize, ySize}; // bottom-right

			// tells which dots to draw based on dice value
			// tl - top-left
			// ml - middle-left
			// bl - bottom-left
			// m - middle
			// tr - top-right
			// mr - middle-right
			// br - bottom-right
			let dotsToDraw = null;
			if (value === 1) {
				dotsToDraw = [dots.m];
			} else if (value === 2) {
				dotsToDraw = [dots.tl, dots.br];
			} else if (value === 3) {
				dotsToDraw = [dots.tl, dots.m, dots.br];
			} else if (value === 4) {
				dotsToDraw = [dots.tl, dots.bl, dots.tr, dots.br];
			} else if (value === 5) {
				dotsToDraw = [dots.tl, dots.bl, dots.m, dots.tr, dots.br];
			} else if (value === 6) {
				dotsToDraw = [dots.tl, dots.ml, dots.bl, dots.tr, dots.mr, dots.br];
			}

			ctx.fillStyle = dotColor;
			ctx.translate(x, y - size / 2); // translate dots to the die face
			for (const dot of dotsToDraw) {
				ctx.beginPath();
				ctx.arc(dot.xSize, dot.ySize, size * 0.105, 0, 2 * Math.PI);
				ctx.fill();
			}

			// reset drawing start point to default
			ctx.restore();
		},
		measure(rc, fontInfo, props, node) {
			const size = fontInfo.size * 2.1;
			/**
			 * The vertical height (and therefore width) of a rotated square is not the same as
			 * its "normal" height, so this calculates the "real" vertical height that the die
			 * will take up on the screen, taking into account the angle the user is asking for.
			 *
			 * In particular, "size" gives the side length of the square and "sizeAdjusted"
			 * is the calculated height/width of the rotated square (so they are equal when
			 * the angle is 0/90/etc. and otherwise sizeAdjusted is a little larger).
			 */
			const {diceAngle: angle} = node;
			const angleRadians = (angle * Math.PI) / 180;
			const sizeAdjusted =
				Math.abs(Math.sin(angleRadians)) * size +
				Math.abs(Math.cos(angleRadians)) * size;
			return {
				width: sizeAdjusted,
				top: sizeAdjusted / 2,
				bottom: sizeAdjusted / 2,
				fontInfo,
				size,
				sizeAdjusted,
			};
		},
	},
	textShape: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				width,
				boundsFontInfo,
				size,
				textRect,
				hasChildren,
				childMeasure,
			} = measure;

			context.save();
			context.lineWidth = getLineWidth(rc, boundsFontInfo) / 1.5;
			const xCenter = x + width / 2;
			const yCenter = y;

			const centroidX = xCenter;
			let centroidY = yCenter;

			context.fillStyle =
				colors[node.fillStyle] ||
				colorSchemes[ColorUtil.getTextShapeDefaultColors()[node.shape]];
			context.strokeStyle = colors["black"];

			if (node.shape === "square") {
				context
					.bp()
					.fillRect(xCenter - size, yCenter - size, 2 * size, 2 * size)
					.strokeRect(xCenter - size, yCenter - size, 2 * size, 2 * size);
			} else if (node.shape === "circle") {
				context
					.bp()
					.arc(xCenter, yCenter, size, 0, 2 * Math.PI)
					.fill()
					.stroke();
			} else if (node.shape === "triangle") {
				context
					.bp()
					.M(xCenter, yCenter - size)
					.L(xCenter - size, yCenter + size)
					.L(xCenter + size, yCenter + size)
					.Z()
					.fill()
					.stroke();

				centroidY += size / 3;
			}
			const scaledTextRect = _.cloneDeep(textRect);
			scaledTextRect[0] = centroidX - scaledTextRect[0];
			scaledTextRect[1] = centroidY - scaledTextRect[1];

			context.restore();

			if (hasChildren) {
				const initTextRect = {
					x: 0,
					y: 0,
					w: childMeasure.width,
					h: childMeasure.top + childMeasure.bottom,
				};
				const scaledRect = {
					x: scaledTextRect[0],
					y: scaledTextRect[1],
					w: scaledTextRect[2],
					h: scaledTextRect[3],
				};

				//Scaling of child
				const fit = Math2D.fitInRect(initTextRect, scaledRect);
				context.save();
				context.translate(fit.translate[0], fit.translate[1]);
				context.scale(fit.scale[0], fit.scale[1]);

				rc.drawNode(rc, 0, childMeasure.top, node.children[0], childMeasure);
				context.restore();
			}
		},
		measure(rc, fontInfo, props, node) {
			const scalingFactor = 1.25;
			const boundsFontInfo = scaleFont(fontInfo, scalingFactor);
			const lineWidth = getLineWidth(rc, boundsFontInfo);
			const fontSize = boundsFontInfo.size;

			const shapeWidth = fontSize;
			const shapeHeight = fontSize;
			const margin = fontSize / 10;
			// size is the side lengths of the rect that surrounds shapeText child.
			// this child is typically text.
			const size = (shapeWidth - 2 * margin) / 2;

			let childMeasure = {};
			const hasChildren = node.children.length;
			if (hasChildren) {
				childMeasure = rc.measureNode(rc, fontInfo, props, node.children[0]);
			}

			const textRect = [size / 2, size / 2, size, size];

			// translates the text location down since centroid is not symmetric
			// in y direction as it is with circle and square.
			if (node.shape === "triangle") {
				textRect[1] -= size / 6;
			}

			return {
				width: shapeWidth,
				top: shapeHeight / 2 + lineWidth / 2,
				bottom: shapeHeight / 2 + lineWidth / 2,
				boundsFontInfo,
				size,
				textRect,
				hasChildren,
				childMeasure,
			};
		},
	},
	shapeNumberCard: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				width,
				cardFontInfo,
				numberFontInfo,
				cardWidth,
				cardHeight,
				borderRadius,
			} = measure;
			const text = node.text;

			let size = cardFontInfo.size / 3.5;
			if (node.size === "small") {
				size /= 2;
			} else if (node.size === "large") {
				size *= 1.7;
			}

			context.save();
			context.lineWidth = getLineWidth(rc, cardFontInfo) / 2;
			const xCenter = x + width / 2;
			const hor = cardWidth / 2 - borderRadius;
			const ver = cardHeight / 2 - borderRadius;
			context
				.bp()
				.M(xCenter - hor - borderRadius, y - ver)
				.arc(xCenter - hor, y - ver, borderRadius, Math.PI, (3 * Math.PI) / 2)
				.arc(xCenter + hor, y - ver, borderRadius, (3 * Math.PI) / 2, 0)
				.arc(xCenter + hor, y + ver, borderRadius, 0, Math.PI / 2)
				.arc(xCenter - hor, y + ver, borderRadius, Math.PI / 2, Math.PI)
				.Z()
				.stroke();

			context.fillStyle = colors[node.color];
			context.strokeStyle = colors["black"];
			if (text === "square") {
				context
					.bp()
					.fillRect(xCenter - size, y - size, 2 * size, 2 * size)
					.strokeRect(xCenter - size, y - size, 2 * size, 2 * size);
			} else if (text === "circle") {
				context
					.bp()
					.arc(xCenter, y, size, 0, 2 * Math.PI)
					.fill()
					.stroke();
			} else if (text === "triangle") {
				context
					.bp()
					.M(xCenter, y - size)
					.L(xCenter - size, y + size)
					.L(xCenter + size, y + size)
					.Z()
					.fill()
					.stroke();
			} else {
				let scale = 0.9;
				if (node.size === "small") {
					scale *= 0.6;
				} else if (node.size === "large") {
					scale *= 1.4;
				}
				const fontInfo = scaleFont(
					node.isSymbol ? cardFontInfo : numberFontInfo,
					scale
				);
				context.font = Font.fromInfo(fontInfo);
				const textWidth = context.measureText(text).width;
				CanvasText.drawMiddleText(
					context,
					text,
					xCenter - textWidth / 2,
					y,
					true
				);
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const cardFontInfo = scaleFont(fontInfo, 1.75);
			// Always use Roboto font to draw numbers on cards
			const numberFontInfo = _.extend({}, scaleFont(fontInfo, 1.25), {
				family: ["Roboto"],
				bold: true,
			});
			const lineWidth = getLineWidth(rc, cardFontInfo);
			const cardHeight = cardFontInfo.size * 1.75;
			const cardWidth = (cardHeight * 2.375) / 3.5;
			const margin = cardWidth / 15;
			const borderRadius = cardWidth / 10;
			return {
				width: cardWidth + 2 * margin,
				top: cardHeight / 2 + lineWidth / 2,
				bottom: cardHeight / 2 + lineWidth / 2,
				cardWidth,
				cardHeight,
				cardFontInfo,
				numberFontInfo,
				borderRadius,
			};
		},
	},
	card: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				width,
				cardFontInfo,
				cardWidth,
				cardHeight,
				borderRadius,
			} = measure;
			const text = node.text;
			const value = node.cardValue;

			context.save();
			context.lineWidth = getLineWidth(rc, cardFontInfo) / 2;
			const xCenter = x + width / 2;
			const hor = cardWidth / 2 - borderRadius;
			const ver = cardHeight / 2 - borderRadius;

			// set symbol fill and card outline color
			context.fillStyle = colors[node.color];
			if (node.outline) {
				context.strokeStyle = "black";
			} else {
				context.strokeStyle = "transparent";
			}

			context
				.bp()
				.M(xCenter - hor - borderRadius, y - ver)
				.arc(xCenter - hor, y - ver, borderRadius, Math.PI, (3 * Math.PI) / 2)
				.arc(xCenter + hor, y - ver, borderRadius, (3 * Math.PI) / 2, 0)
				.arc(xCenter + hor, y + ver, borderRadius, 0, Math.PI / 2)
				.arc(xCenter - hor, y + ver, borderRadius, Math.PI / 2, Math.PI)
				.Z()
				.stroke();

			// define suit symbol locations
			const suits = {};
			const padding = 0.2;
			let xSize = padding * cardWidth;
			let ySize = padding * cardHeight;
			suits.tl = {xSize, ySize}; // top-left
			ySize = (padding + 1) * cardHeight * (1 / 3);
			suits.sftl = {xSize, ySize}; // second-from-top-left
			ySize = cardHeight * 0.5;
			suits.ml = {xSize, ySize}; // middle-left
			ySize = cardHeight * (2 - padding) * (1 / 3);
			suits.sfbl = {xSize, ySize}; // second-from-bottom-left
			ySize = cardHeight * (1 - padding);
			suits.bl = {xSize, ySize}; // bottom-left
			xSize = cardWidth * 0.5;
			ySize = padding * cardHeight;
			suits.tm = {xSize, ySize}; // top-middle
			ySize = (padding + 0.5) * 0.5 * cardHeight;
			suits.sftm = {xSize, ySize}; // second-from-top-middle
			ySize = cardHeight * 0.5;
			suits.c = {xSize, ySize}; // center
			ySize = cardHeight * (1.5 - padding) * 0.5;
			suits.sfbm = {xSize, ySize}; // second-from-bottom-middle
			ySize = cardHeight * (1 - padding);
			suits.bm = {xSize, ySize}; // bottom-middle
			xSize = cardWidth * (1 - padding);
			ySize = padding * cardHeight;
			suits.tr = {xSize, ySize}; // top-right
			ySize = (padding + 1) * cardHeight * (1 / 3);
			suits.sftr = {xSize, ySize}; // second-from-top-right
			ySize = cardHeight * 0.5;
			suits.mr = {xSize, ySize}; // middle-right
			ySize = cardHeight * (2 - padding) * (1 / 3);
			suits.sfbr = {xSize, ySize}; // second-from-bottom-right
			ySize = cardHeight * (1 - padding);
			suits.br = {xSize, ySize}; // bottom-right

			// tells which dots to draw based on dice value
			// tl - top-left
			// sftl - second-from-top-left
			// ml - middle-left
			// sfbl - second-from-bottom-left
			// bl - bottom-left
			// tm - top-middle
			// sftm - second-from-top-middle
			// c - center
			// sfbm - second-from-bottom-middle
			// bm - bottom-middle
			// tr - top-right
			// sftr - second-from-top-right
			// mr - middle-right
			// sfbr - second-from-bottom-right
			// br - bottom-right
			let suitsToDraw = null;
			if (value === 2) {
				suitsToDraw = [suits.tm, suits.bm];
			} else if (value === 3) {
				suitsToDraw = [suits.tm, suits.c, suits.bm];
			} else if (value === 4) {
				suitsToDraw = [suits.tl, suits.bl, suits.tr, suits.br];
			} else if (value === 5) {
				suitsToDraw = [suits.tl, suits.bl, suits.c, suits.tr, suits.br];
			} else if (value === 6) {
				suitsToDraw = [
					suits.tl,
					suits.ml,
					suits.bl,
					suits.tr,
					suits.mr,
					suits.br,
				];
			} else if (value === 7) {
				suitsToDraw = [
					suits.tl,
					suits.ml,
					suits.bl,
					suits.sftm,
					suits.tr,
					suits.mr,
					suits.br,
				];
			} else if (value === 8) {
				suitsToDraw = [
					suits.tl,
					suits.ml,
					suits.bl,
					suits.sftm,
					suits.sfbm,
					suits.tr,
					suits.mr,
					suits.br,
				];
			} else if (value === 9) {
				suitsToDraw = [
					suits.tl,
					suits.sftl,
					suits.sfbl,
					suits.bl,
					suits.c,
					suits.tr,
					suits.sftr,
					suits.sfbr,
					suits.br,
				];
			} else if (value === 10) {
				suitsToDraw = [
					suits.tl,
					suits.sftl,
					suits.sfbl,
					suits.bl,
					suits.sftm,
					suits.sfbm,
					suits.tr,
					suits.sftr,
					suits.sfbr,
					suits.br,
				];
			}

			// translate suit symbols to the card
			context.translate(x + cardWidth / 15, y - cardHeight / 2);

			const scale = 0.9;
			const fontInfo = scaleFont(cardFontInfo, scale);
			context.font = Font.fromInfo(fontInfo);
			const textWidth = context.measureText(text).width;
			context.strokeStyle = colors[node.color];

			// draw suit symbols
			for (const suit of suitsToDraw) {
				context.beginPath();
				CanvasText.drawMiddleText(
					context,
					text,
					suit.xSize - textWidth / 2,
					suit.ySize,
					true
				);
				context.fill();
			}

			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const cardHeightScaler = scaleFont(fontInfo, 4.6);
			// Always use sans-serif font to draw suits on cards
			const cardFontInfo = _.extend({}, scaleFont(fontInfo, 1.5), {
				family: ["sans-serif"],
				bold: false,
			});
			const lineWidth = getLineWidth(rc, cardFontInfo);
			const cardHeight = cardHeightScaler.size * 1.75;
			const cardWidth = (cardHeight * 2.375) / 3.5;
			const margin = cardWidth / 15;
			const borderRadius = cardWidth / 10;
			return {
				width: cardWidth + 2 * margin,
				top: cardHeight / 2 + lineWidth / 2,
				bottom: cardHeight / 2 + lineWidth / 2,
				cardWidth,
				cardHeight,
				cardFontInfo,
				borderRadius,
			};
		},
	},
	inputCircleButton: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			context.save();
			context.font = Font.fromInfo(measure.fontInfo);
			drawInputCircleButton(
				context,
				{
					x: x,
					y: y - measure.top,
					w: measure.width,
					h: measure.top + measure.bottom,
				},
				null,
				node.inputType,
				node.selected
			);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const size = fontInfo.size * 1.5;
			return {
				width: size,
				top: size / 2,
				bottom: size / 2,
				fontInfo,
			};
		},
	},
	zigzagPuzzle: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const numbers = node.children[0].children;
			const {maxWidth, maxHeight, numMeasures, space, fontInfo} = measure;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = getLineWidth(rc, fontInfo);
			let xPos = x + numMeasures[0][0] / 2;
			numbers.forEach((num, i) => {
				const mult = i % 2 ? -1 : 1;
				const yPos = Math.min(maxHeight * 1.5, (maxHeight * maxWidth) / space);
				rc.drawNode(rc, xPos - numMeasures[i][0] / 2, y + mult * yPos, num);
				if (i < numMeasures.length - 1) {
					context
						.bp()
						.M(xPos, y - mult * maxHeight)
						.L(xPos + space, y + mult * maxHeight)
						.stroke();
				}
				xPos += space;
			});
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const numbers = node.children[0].children;
			const numMeasures = [];
			let maxHeight = 0;
			let maxWidth = 0;
			numbers.forEach((num, i) => {
				const measure = rc.measureNode(rc, fontInfo, props, num);
				maxHeight = Math.max(maxHeight, measure.top + measure.bottom);
				maxWidth = Math.max(maxWidth, measure.width);
				numMeasures.push([measure.width, measure.top + measure.bottom]);
			});
			const space =
				Math.max(maxWidth + getDigitWidth(rc, fontInfo), 2 * maxHeight) *
				node.size;
			const top =
				Math.min(maxHeight * 1.5, (maxHeight / space) * maxWidth) +
				maxHeight / 2;
			return {
				width:
					space * (numbers.length - 1) +
					(numMeasures[0][0] + numMeasures[numbers.length - 1][0]) / 2,
				top,
				bottom: top,
				space,
				fontInfo,
				numMeasures,
				maxHeight,
				maxWidth,
			};
		},
	},
	mcnugget: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width, top, max, min} = measure;

			const topY = y - top;
			const lineWidth = getLineWidth(rc, fontInfo);

			let count = 1;
			const willShade = [];
			const squareSize = width / max;
			let startX;
			let startY;
			let numWidth;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			context.beginPath();
			for (let j = 0; j < min; j++) {
				for (let i = 0; i < max; i++) {
					context.strokeStyle = ColorUtil.getCurrentScheme().lightGrayFill;
					startX = x + i * squareSize;
					startY = topY + j * squareSize;
					willShade.push(i * max + j * min);
					willShade.push(j * max + i * min);
					if (node.shaded) {
						if (_.indexOf(willShade, count) > -1) {
							context.fillStyle = ColorUtil.getCurrentScheme().lighterGrayFill;
							context.fillRect(startX, startY, squareSize, squareSize);
						}
					}
					context.strokeRect(startX, startY, squareSize, squareSize);
					numWidth = context.measureText(count.toString()).width;
					context.fillStyle = ColorUtil.getCurrentScheme().defaultText;
					CanvasText.drawMiddleText(
						context,
						count.toString(),
						startX + (squareSize - numWidth) / 2,
						startY + (squareSize - fontInfo.size) + lineWidth
					);
					count++;
				}
			}
			context.closePath();
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const chickens = node.chickens;
			const max = _.max(chickens);
			const min = _.min(chickens);
			const maxNum = max * min;
			const maxLen = maxNum.toString().length;
			const lineWidth = getLineWidth(rc, fontInfo);
			const maxNumSize = maxLen * getDigitWidth(rc, fontInfo);
			const squareSize = maxNumSize + lineWidth * 14;
			return {
				width: max * squareSize,
				top: (min * squareSize) / 2,
				bottom: (min * squareSize) / 2,
				fontInfo,
				//extra
				max: max,
				min: min,
			};
		},
	},
	hundredsChartGrid: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, top, squareSize} = measure;

			const topY = y - top;
			const lineWidth = getLineWidth(rc, fontInfo);

			const {
				defaultBlackFill: strokeStyle,
				defaultText: fillStyle,
			} = ColorUtil.getCurrentScheme();

			let count = 1;
			const gridSize = 10;
			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			context.strokeStyle = strokeStyle;
			context.fillStyle = fillStyle;
			context.beginPath();
			for (let row = 0; row < gridSize; ++row) {
				for (let col = 0; col < gridSize; ++col) {
					const text = count.toString();
					const startX = x + squareSize * col;
					const startY = topY + squareSize * row;
					const multiples = node.multiples.sort().filter(m => count % m === 0);
					if (multiples.length) {
						let color = Color(ColorUtil.getMultipleColors(multiples[0]));
						for (let k = 1; k < multiples.length; k++) {
							const multipleColor = Color(
								ColorUtil.getMultipleColors(multiples[k])
							);
							color = color.mix(multipleColor, 1 / (k + 1));
						}
						context.globalAlpha = Math.min(
							0.3 + 0.35 * (multiples.length - 1),
							1
						);
						context.bp().fill(color.string());
						context.fillRect(startX, startY, squareSize, squareSize);
					}
					context.globalAlpha = 1;
					context.fillStyle = fillStyle;
					context.strokeRect(startX, startY, squareSize, squareSize);
					const numWidth = context.measureText(text).width;
					CanvasText.drawMiddleText(
						context,
						text,
						startX + (squareSize - numWidth) / 2,
						startY + squareSize / 2 + lineWidth / 2
					);
					++count;
				}
			}
			context.closePath();
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const lineWidth = getLineWidth(rc, fontInfo);
			const maxNumSize = 3 * getDigitWidth(rc, fontInfo);
			const squareSize = maxNumSize + lineWidth * 4;
			return {
				squareSize,
				width: 10 * squareSize,
				top: 5 * squareSize,
				bottom: 5 * squareSize,
				fontInfo,
			};
		},
	},
	hundredsChartSection: {
		draw(rc, x, y, node, measure) {
			const {context} = rc;
			context.save();

			// setup default values
			const {
				fontInfo,
				top,
				squareSize,
				margin,
				MIN_INDEX,
				MAX_INDEX,
				minRow,
				minCol,
				maxRow,
				maxCol,
			} = measure;
			const {options} = node;
			const {gridSize, padColor, blanks = []} = options;
			const topY = y - top;
			const lineWidth = getLineWidth(rc, fontInfo);
			const {
				lighterGrayFill: strokeStyle,
				defaultBlackFill: fillStyle, // also used for text
			} = ColorUtil.getCurrentScheme();
			const padStyle = ColorUtil.getDiagramColors()[padColor];

			// context defaults
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			context.strokeStyle = strokeStyle;
			context.fillStyle = fillStyle;
			context.globalAlpha = 1;

			// draw the table!
			context.beginPath();
			for (let row = minRow; row <= maxRow; ++row) {
				const rowIndex = row - minRow; // 0-indexed row
				for (let col = minCol; col <= maxCol; ++col) {
					const count = row * gridSize + col + 1;
					const text = count.toString();
					const colIndex = col - minCol; // 0-indexed col
					const startX = x + squareSize * colIndex;
					const startY = topY + squareSize * rowIndex;
					const rect = [startX, startY, squareSize, squareSize];
					const paddedRect = Math2D.padRect(rect, -margin);
					const isPadding =
						row < MIN_INDEX ||
						row > MAX_INDEX ||
						col < MIN_INDEX ||
						col > MAX_INDEX;

					if (isPadding) {
						context.fillStyle = padStyle;
						context.strokeStyle = padStyle;
						context.fillRect(...rect);
						context.strokeRect(...rect); // cover 1px outlines
						context.fillStyle = fillStyle;
						context.strokeStyle = strokeStyle;
					} else {
						const [padX, padY, padWidth, padHeight] = paddedRect;
						if (blanks.includes(count)) {
							rc.pushBlank({
								type: "simple",
								x: padX,
								y: padY,
								width: padWidth,
								height: padHeight,
								answerSize: text.length,
							});
						} else {
							const numWidth = context.measureText(text).width;
							CanvasText.drawMiddleText(
								context,
								text,
								startX + (squareSize - numWidth) / 2,
								startY + squareSize / 2 + lineWidth / 2
							);
							context.strokeRect(...paddedRect);
						}
					}
				}
			}

			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const gridDimensions = this.getGridDimensions(node.options);
			const {minRow, minCol, maxRow, maxCol} = gridDimensions;
			const numRows = maxRow - minRow + 1;
			const numCols = maxCol - minCol + 1;

			const lineWidth = getLineWidth(rc, fontInfo);
			const maxNumSize = 3 * getDigitWidth(rc, fontInfo);
			const squareSize = maxNumSize + lineWidth * 4;
			const margin = squareSize * 0.04;

			// measure children for the cache entry
			node.children.forEach(child => {
				rc.measureNode(rc, fontInfo, props, child);
			});

			return _.extend(
				{
					squareSize,
					numRows,
					numCols,
					width: numCols * squareSize,
					top: (numRows / 2) * squareSize,
					bottom: (numRows / 2) * squareSize,
					fontInfo,
					margin,
				},
				gridDimensions
			);
		},
		getGridDimensions(options) {
			const {
				gridSize,
				center: sectionCenter,
				width: sectionWidth,
				height: sectionHeight,
				top: focusTop = false,
				bottom: focusBottom = false,
				left: focusLeft = false,
				right: focusRight = false,
			} = options;
			const MIN_INDEX = 0;
			const MAX_INDEX = gridSize - 1;
			let minRow = MIN_INDEX;
			let minCol = MIN_INDEX;
			let maxRow = MAX_INDEX;
			let maxCol = MAX_INDEX;

			if (sectionCenter) {
				// fancy logic to get the correct section rows and columns
				const centerRow = Math.floor((sectionCenter - 1) / gridSize);
				const centerCol = (sectionCenter % gridSize || gridSize) - 1;
				minRow =
					centerRow - Math.floor(sectionHeight / 2) + Number(focusBottom);
				maxRow = centerRow + Math.floor(sectionHeight / 2) - Number(focusTop);
				minCol = centerCol - Math.floor(sectionWidth / 2) + Number(focusRight);
				maxCol = centerCol + Math.floor(sectionWidth / 2) - Number(focusLeft);
			}

			return {
				MIN_INDEX,
				MAX_INDEX,
				minRow,
				minCol,
				maxRow,
				maxCol,
			};
		},
	},
	hundredsChartSquare: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {lineWidth, width, top, bottom, side} = measure;
			context.save();
			context.lineWidth = lineWidth;
			context
				.bp()
				.rect(
					x + lineWidth / 2,
					y - top + lineWidth / 2,
					width - lineWidth,
					top + bottom - lineWidth
				)
				.stroke();
			context.restore();
			MultiplePattern.drawMultiplePattern(
				context,
				[x + lineWidth, y - top + lineWidth, side, side],
				node.multiples
			);
		},
		measure(rc, fontInfo, props, node) {
			const side = fontInfo.size;
			const lw = getLineWidth(rc, fontInfo);
			return {
				width: side + 2 * lw,
				top: side / 2 + lw,
				bottom: side / 2 + lw,
				fontInfo,
				side: side,
				lineWidth: lw,
			};
		},
	},
	botarrow: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {width, top, fontInfo} = measure;
			const tail = width * 0.65;
			const fatness = fontInfo.size * 0.4;
			let label = node.label;
			const color = ColorUtil.getCurrentScheme();
			const op = label.charAt(0);
			let fillColor = color.defaultText;
			if (op === "*") {
				fillColor = color.botMult;
				label = label.replace(op, Operators.computeText(op));
			} else if (op === "+") {
				fillColor = color.botAdd;
			} else if (op === "-") {
				fillColor = color.botSub;
			} else if (op === "/") {
				fillColor = color.botDiv;
			}
			if (label === "/2") {
				label = "half";
			}
			//Draw arrow shape
			context
				.sv()
				.bp()
				.M(x, y - fatness)
				.L(x + tail, y - fatness)
				.L(x + tail, y - top)
				.L(x + width, y)
				.L(x + tail, y + top)
				.L(x + tail, y + fatness)
				.L(x, y + fatness)
				.Z()
				.fill(fillColor)
				.rs();
			if (label) {
				context.save();
				context.font = Font.scaleSize(Font.fromInfo(fontInfo), 0.6);
				context.fillStyle = color.defaultWhiteFill;
				const textWidth = context.measureText(label).width;
				CanvasText.drawMiddleText(
					context,
					label,
					x + (width * 0.9 - textWidth) / 2,
					y
				);
				context.restore();
			}
		},
		measure(rc, fontInfo, props, node) {
			const fontSize = fontInfo.size;
			return {
				width: fontSize * 1.5,
				top: fontSize / 2,
				bottom: fontSize / 2,
				fontInfo,
			};
		},
	},
	blank: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {blankType, answerSize, initialValue, isExponent} = node;
			const {
				fontInfo,
				width,
				top,
				bottom,
				xBuffer,
				xPadding,
				fontSizeMultiplier,
				fracSizeMultiplier,
				blankMeasure,
				fractionBlankMeasure,
			} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);

			context.save();
			context.beginPath();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;
			const lineOffset = lineWidth / 4;

			// NOTE: These blank information objects pushed to the render controller may be ignored
			// if a cached canvas. You also need to update BatexView.js's createBlankView if you make
			// changes here.
			if (blankType === "fraction") {
				context.beginPath();
				context.moveTo(x, y + lineOffset);
				context.lineTo(x + width, y + lineOffset);
				context.stroke();

				rc.pushBlank({
					type: "fraction",
					x: x,
					y: y - top,
					width: width,
					height: top + bottom,
					typeInfo: {
						numerator: {
							x: x + xPadding,
							y: y - top,
							width: fractionBlankMeasure.width,
							height: fractionBlankMeasure.top + fractionBlankMeasure.bottom,
						},
						denominator: {
							x: x + xPadding,
							y:
								y +
								bottom -
								(fractionBlankMeasure.top + fractionBlankMeasure.bottom),
							width: fractionBlankMeasure.width,
							height: fractionBlankMeasure.top + fractionBlankMeasure.bottom,
						},
					},
					answerSize,
					initialValue,
					fontSizeMultiplier,
					fracSizeMultiplier,
				});
			} else if (blankType === "mixed") {
				context.beginPath();
				context.moveTo(x + blankMeasure.width + 2 * xPadding, y + lineOffset);
				context.lineTo(x + width, y + lineOffset);
				context.stroke();

				rc.pushBlank({
					type: "mixed",
					x: x,
					y: y - top,
					width: width,
					height: top + bottom,
					typeInfo: {
						whole: {
							x: x,
							y: y - blankMeasure.top,
							width: blankMeasure.width,
							height: blankMeasure.top + blankMeasure.bottom,
						},
						numerator: {
							x: x + blankMeasure.width + 3 * xPadding,
							y: y - top,
							width: fractionBlankMeasure.width,
							height: fractionBlankMeasure.top + fractionBlankMeasure.bottom,
						},
						denominator: {
							x: x + blankMeasure.width + 3 * xPadding,
							y:
								y +
								bottom -
								fractionBlankMeasure.top -
								fractionBlankMeasure.bottom,
							width: fractionBlankMeasure.width,
							height: fractionBlankMeasure.top + fractionBlankMeasure.bottom,
						},
					},
					wholeSize: node.wholeSize,
					fracSize: node.fracSize,
					initialValue,
					fontSizeMultiplier,
					fracSizeMultiplier,
				});
			} else {
				rc.pushBlank({
					type: "simple",
					x: x + xBuffer,
					y: y - top,
					width: width - 2 * xBuffer,
					height: top + bottom,
					typeInfo: {},
					answerSize,
					initialValue,
					isExponent: !!isExponent,
					fontSizeMultiplier,
					backgroundColor: node.shaded
						? ColorUtil.getCurrentScheme().lighterGrayFill
						: null,
					outlineColor: ColorUtil.translateToDiagramColor(node.outlineColor),
				});
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const naturalMeasure = Helpers.getNaturalBlankMeasure(rc, fontInfo);
			const {xBuffer, yBuffer} = naturalMeasure;

			let blankMeasure, fractionBlankMeasure, fractionSizingRatio;
			const getLargestPossibleAnswer = function(size) {
				let characters = _.fill(
					Array(props.keyboardContainsOnlyLetters ? size : size + 1),
					props.largestBlankCharacter
				);
				if (props.blanksContainOperators && !props.measureWithoutOperators) {
					characters = characters.map((ch, ind) =>
						ind % 2 === 1 ? " + " : ch
					);
					if (characters.length % 2 === 1) {
						characters.push(" ");
					}
				}
				return characters.join("");
			};
			if (props.overrideBlankSize) {
				blankMeasure = {
					width: props.overrideBlankSize.w + xBuffer * 2,
					top: props.overrideBlankSize.h / 2,
					bottom: props.overrideBlankSize.h / 2,
					fontInfo,
					xBuffer,
					fontSizeMultiplier: 1,
				};
				fractionSizingRatio = 1;
				fractionBlankMeasure = blankMeasure;
			} else {
				const answerSize =
					node.blankType === "mixed"
						? node.wholeSize
						: parseInt(node.answerSize, 10);
				let answer = getLargestPossibleAnswer(answerSize);
				const measureFontInfo = _.extend({family: ["lizzie"]}, fontInfo);
				context.save();
				context.font = Font.fromInfo(measureFontInfo);
				const blankAnsMeasure = context.measureText(answer);
				context.restore();

				blankMeasure = {
					width: blankAnsMeasure.width + xBuffer * 2,
					top:
						naturalMeasure.top +
						(node.vPadding || 0) * getLineWidth(rc, fontInfo),
					bottom: naturalMeasure.bottom,
					fontInfo,
					xBuffer,
					fontSizeMultiplier: measureFontInfo.size / props.baseFontSize,
				};
				let fullWidth = blankAnsMeasure.width;
				if (node.blankType === "mixed" && node.wholeSize !== node.fracSize) {
					answer = getLargestPossibleAnswer(node.fracSize);
					context.save();
					context.font = Font.fromInfo(measureFontInfo);
					fullWidth = context.measureText(answer).width;
					context.restore();
				}
				fractionSizingRatio = 0.8;
				fractionBlankMeasure = {
					width: fullWidth * fractionSizingRatio + xBuffer * 2,
					top: (measureFontInfo.size * fractionSizingRatio) / 2 + yBuffer,
					bottom: (measureFontInfo.size * fractionSizingRatio) / 2 + yBuffer,
					fontInfo,
					xBuffer,
				};
			}
			const xPadding = fontInfo.size / 12;
			if (node.blankType === "fraction") {
				return {
					width: fractionBlankMeasure.width + 2 * xPadding,
					top: fractionBlankMeasure.top + fractionBlankMeasure.bottom + yBuffer,
					bottom:
						fractionBlankMeasure.top + fractionBlankMeasure.bottom + yBuffer,
					fontInfo,
					fractionBlankMeasure,
					xPadding,
					xBuffer,
					fractionSizingRatio,
					fontSizeMultiplier: fontInfo.size / props.baseFontSize,
				};
			} else if (node.blankType === "mixed") {
				return {
					width: blankMeasure.width + fractionBlankMeasure.width + 4 * xPadding,
					top: fractionBlankMeasure.top + fractionBlankMeasure.bottom + yBuffer,
					bottom:
						fractionBlankMeasure.top + fractionBlankMeasure.bottom + yBuffer,
					fontInfo,
					fractionBlankMeasure,
					blankMeasure,
					xPadding,
					xBuffer,
					fractionSizingRatio,
					fontSizeMultiplier: fontInfo.size / props.baseFontSize,
				};
			}
			return blankMeasure;
		},
	},
	fakeblank: {
		draw(rc, x, y, node, measure) {
			const {fontInfo, width, top, bottom, xBuffer, blurSize} = measure;
			const context = rc.context;
			const rect = [x + xBuffer, y - top, width - 2 * xBuffer, top + bottom];
			const rectProps = {
				blurSize,
				outlineColor: ColorUtil.translateToDiagramColor(node.outlineColor),
				hasThickerOutline: !!node.outlineColor,
			};
			if (node.shaded)
				rectProps.fillColor = ColorUtil.getCurrentScheme().lighterGrayFill;
			if (node.bgColor) {
				rectProps.fillColor = node.bgColor;
			}

			if (node.initialValue) {
				DrawRectUtil.drawFlatRect(rc.context, rect, rectProps);
				context.save();
				const val = node.initialValue;
				let color = node.color
					? ColorUtil.getCurrentScheme()[node.color]
					: null;
				if (!color) color = ColorUtil.getCurrentScheme().inputText;
				context.fillStyle = color;

				context.font = Font.fromInfo(
					_.extend({}, fontInfo, {family: ["lizzie"]})
				);
				const textMeasure = context.measureText(val).width;
				CanvasText.drawMiddleText(
					context,
					val,
					x + (width - textMeasure) / 2,
					y
				);
				context.restore();
			} else {
				DrawRectUtil.drawInsetRect(rc.context, rect, rectProps);
			}
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, fontInfo);
			const xBuffer = getSpaceWidth(rc, fontInfo) / 3;
			const characterToMeasure = props.largestBlankCharacter;
			const answerSize = node.answerSize;
			let answer = "";
			let c;
			for (c = -1; c < answerSize; c++) {
				answer += characterToMeasure;
			}
			const measureFontInfo = _.extend({family: ["lizzie"]}, fontInfo);
			context.save();
			context.font = Font.fromInfo(measureFontInfo);
			const blankAnsMeasure = context.measureText(answer);
			context.restore();

			const yBuffer = 2 * lineWidth;
			return {
				width: blankAnsMeasure.width + xBuffer * 2,
				top: measureFontInfo.size / 2 + yBuffer,
				bottom: measureFontInfo.size / 2 + yBuffer,
				fontInfo,
				xBuffer,
				fontSizeMultiplier: measureFontInfo.size / props.baseFontSize,
				blurSize: props.insetBlurSize || 0,
			};
		},
	},
	varbox: {
		draw(rc, x, y, node, measure) {
			const {fontInfo, width, top, bottom, xBuffer, fillColor} = measure;
			const context = rc.context;
			const rect = [x + xBuffer, y - top, width - 2 * xBuffer, top + bottom];
			context.save();
			context
				.bp()
				.rect(rect)
				.fill(fillColor);
			if (node.initialValue) {
				const val = node.initialValue;
				context.fillStyle = ColorUtil.getCurrentScheme().inputText;
				context.font = Font.fromInfo(
					_.extend({}, fontInfo, {family: ["lizzie"]})
				);
				const textMeasure = context.measureText(val).width;
				CanvasText.drawMiddleText(
					context,
					val,
					x + (width - textMeasure) / 2,
					y
				);
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, fontInfo);
			const xBuffer = getSpaceWidth(rc, fontInfo) / 3;
			const characterToMeasure = props.largestBlankCharacter;
			const answerSize = node.answerSize;
			let answer = "";
			let c;
			for (c = -1; c < answerSize; c++) {
				answer += characterToMeasure;
			}
			const measureFontInfo = _.extend({}, fontInfo, {family: ["lizzie"]});
			context.save();
			context.font = Font.fromInfo(measureFontInfo);
			const blankAnsMeasure = context.measureText(answer);
			context.restore();

			const fillColor = colors[node.colorName];
			if (node.colorName && !fillColor) {
				rc.pushError(node.colorName + " is not one of the available colors.");
			}

			const yBuffer = 2 * lineWidth;
			return {
				width: blankAnsMeasure.width + xBuffer * 2,
				top: measureFontInfo.size / 2 + yBuffer,
				bottom: measureFontInfo.size / 2 + yBuffer,
				fontInfo,
				xBuffer,
				fillColor,
				fontSizeMultiplier: measureFontInfo.size / props.baseFontSize,
			};
		},
	},
	colorPositionText: {
		draw(rc, x, y, node, measure) {
			const {color, indices} = node;
			const onPreDraw = (i, child) => {
				rc.context.save();
				if (child.type === "variable") {
					const text = child.children[0].text;
					if (
						_.intersection(
							_.map(_.range(text.length), j => i + j),
							indices
						).length
					) {
						rc.context.fillStyle = color;
						rc.context.strokeStyle = color;
					}
					return i + text.length;
				} else if (_.includes(indices, i) && child.type === "symbol") {
					child.color = color;
				} else if (_.includes(indices, i)) {
					rc.context.fillStyle = color;
					rc.context.strokeStyle = color;
				}
				return i + 1;
			};
			const onPostDraw = () => {
				rc.context.restore();
			};
			return LineLayout.drawInline(rc, x, y, node.children, {
				onPreDraw,
				onPostDraw,
			});
		},
		measure(rc, fontInfo, props, node) {
			return LineLayout.measureInline(rc, fontInfo, props, node.children);
		},
	},
	math: {
		draw(rc, x, y, node, measure) {
			return LineLayout.drawInline(rc, x, y, node.children);
		},
		measure(rc, fontInfo, props, node) {
			return LineLayout.measureInline(rc, fontInfo, props, node.children);
		},
	},
	nonmath: {
		draw(rc, x, y, node, measure) {
			return LineLayout.drawInline(rc, x, y, node.children);
		},
		measure(rc, fontInfo, props, node) {
			return LineLayout.measureInline(rc, fontInfo, props, node.children);
		},
	},
	blockMath: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			const {children} = node;
			let currX = x;
			let xOffset;
			for (let i = 0; i < children.length; i += 1) {
				xOffset = rc.getMeasure(children[i]).xPadding || 0;
				rc.drawNode(rc, currX + xOffset, y + measure.top, children[i]);
				currX += computeNodeFullWidth(rc.getMeasure(children[i]));
			}
		},
		measure(rc, fontInfo, props, node) {
			let childMeasure;
			let currWidth = 0;
			let maxTop = 0;
			let maxBottom = 0;
			let yPadding = 0;
			for (let i = 0; i < node.children.length; i += 1) {
				childMeasure = rc.measureNode(rc, fontInfo, props, node.children[i]);
				yPadding = childMeasure.yPadding || 0;
				currWidth += computeNodeFullWidth(childMeasure);
				const childTop = childMeasure.hasOwnProperty("top")
					? childMeasure.top
					: childMeasure.height / 2;
				const childBottom = childMeasure.hasOwnProperty("bottom")
					? childMeasure.bottom
					: childMeasure.height / 2;
				maxTop = Math.max(maxTop, childTop + yPadding);
				maxBottom = Math.max(maxBottom, childBottom + yPadding);
			}
			return {
				width: currWidth,
				height: maxTop + maxBottom,
				top: maxTop,
				bottom: maxBottom,
				fontInfo,
			};
		},
	},
	parbox: {
		draw(rc, x, y, node, measure) {
			return rc.drawNode(rc, x, y - measure.top, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const maxLineWidth = node.widthCoeff * getDigitWidth(rc, fontInfo);
			const realProps = _.extend({}, props, {maxLineWidth});
			const baseMeasure = rc.measureNode(
				rc,
				fontInfo,
				realProps,
				node.children[0]
			);
			return {
				width: Math.min(maxLineWidth, baseMeasure.width),
				top: baseMeasure.height / 2,
				bottom: baseMeasure.height / 2,
			};
		},
	},
	wrappingItem: {
		draw(rc, x, y, node, measure) {
			return LineLayout.drawWrapping(rc, x, y, node, measure);
		},
		measure(rc, fontInfo, props, node) {
			return LineLayout.measureWrapping(rc, fontInfo, props, node, {
				yPadding: fontInfo.size / 12,
				preventCenter: true,
			});
		},
	},
	wrapping: {
		draw(rc, x, y, node, measure) {
			return LineLayout.drawWrapping(rc, x, y, node, measure);
		},
		measure(rc, fontInfo, props, node) {
			return LineLayout.measureWrapping(rc, fontInfo, props, node);
		},
	},
	paragraph: {
		isBlock: true,
		draw(rc, x, y, node, measure) {
			return rc.drawNode(rc, x, y, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			return rc.measureNode(rc, fontInfo, props, node.children[0]);
		},
	},
	circleCrop: {
		draw(rc, x, y, node, measure) {
			const {context} = rc;
			const {options} = node;
			const {glass} = options;
			const {defaultBlackFill: strokeStyle} = ColorUtil.getCurrentScheme();
			const {width, diameter, childMeasure} = measure;
			const {width: childWidth} = childMeasure;
			// define where the left-side of the diagram should be such that it's in the middle of this node
			const childX = x + (width - childWidth) / 2;
			// child should be vertically aligned with this node, so y should be the same
			const childY = y;

			rc.drawNode(rc, childX, childY, node.children[0]);
			context.save();
			context.beginPath();

			const centerX = childX + childWidth / 2;
			if (!glass) {
				// outline circle
				context.strokeStyle = strokeStyle;
				context.arc(centerX, childY, diameter / 2, 0, Math.PI * 2);
				context.stroke();
			}

			// crop to circle
			context.globalCompositeOperation = "destination-in";
			context.arc(centerX, childY, diameter / 2, 0, Math.PI * 2);
			context.fill();

			if (glass) {
				CircleCropUtil.drawLookingGlass(context, centerX, y, diameter / 2);
			}

			// uncomment to draw guidelines, useful for debugging
			// const {top: childTop, bottom: childBottom} = childMeasure;
			// const childHeight = childTop + childBottom;
			// const {height, top} = measure;
			// context.globalCompositeOperation = "source-over";
			// context.strokeRect(x, y - top, width, height);
			// context.strokeRect(childX, childY - childTop, childWidth, childHeight);
			// context.arc(centerX, childY, diameter / 2, 0, Math.PI * 2);
			// context.stroke();

			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const {options} = node;
			const {glass} = options;
			const childMeasure = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			const {
				width: childWidth,
				top: childTop,
				bottom: childBottom,
			} = childMeasure;
			const childHeight = childTop + childBottom;
			const diameter = Math.min(childWidth, childHeight);

			// extend height to make room for glass' border
			let top = childTop;
			let bottom = childBottom;
			let width = childWidth;
			if (glass) {
				const {nodeScaleX, nodeScaleY} = CircleCropUtil.getAssetAttributes(
					diameter / 2
				);
				top *= nodeScaleY;
				bottom *= nodeScaleY;
				// Note that we're extending the width (when using the looking glass asset) such that the
				// child diagram can be in the center of this node while still fitting the looking glass.
				// This is so that the problem itself (i.e. child diagram) can be centered on the problem page.
				width *= nodeScaleX;
			}
			const height = top + bottom;

			return {
				diameter,
				width,
				height,
				top,
				bottom,
				childMeasure,
			};
		},
	},
	customCommand: NodesCustom.customCommand,
	customEnvironment: NodesCustom.customEnvironment,
	customInlineEnvironment: NodesCustom.customInlineEnvironment,
	longDivisionBracket: NodesLongOps.longDivisionBracket,
	longDivision: NodesLongOps.longDivision,
	longMultiplication: NodesLongOps.longMultiplication,
	longSimpleOperation: NodesLongOps.longSimpleOperation,
	arrow: NodesArrows.arrow,
	linearrow: NodesArrows.linearrow,
	dayArrowSequence: NodesArrows.dayArrowSequence,
	arrowList: NodesArrows.arrowList,
	equalArrows: NodesArrows.equalArrows,
	distributionArrows: NodesArrows.distributionArrows,
	bouncyArrow: NodesArrows.bouncyArrow,
	image: NodesImages.image,
	coins: NodesImages.coins,
	blocks: NodesImages.blocks,
	btenblocks: NodesImages.btenblocks,
	clock: NodesImages.clock,
	balanceScale: NodesImages.balanceScale,
	measuringCup: NodesImages.measuringCup,
	graduatedCylinder: NodesImages.graduatedCylinder,
	thermometer: NodesImages.thermometer,
	modibot: NodesImages.modibot,
	alignLine: NodesEnvironments.alignLine,
	alignMath: NodesEnvironments.alignMath,
	item: NodesEnvironments.item,
	itemList: NodesEnvironments.itemList,
	tableRow: NodesEnvironments.tableRow,
	table: NodesEnvironments.table,
	optreeRow: NodesEnvironments.optreeRow,
	optree: NodesEnvironments.optree,
	mistake: NodesEnvironments.mistake,
	numberline: NodesEnvironments.numberline,
	blockDiagram: NodesEnvironments.blockDiagram,
	...CommandIndex.nodes,
};

module.exports = {
	nodeData,
};
